/**
 * src/components/spatial/SpatialObjectsAdminOverlay.tsx
 *
 * GeoTrust mirror of the GeoLink Spatial Objects admin dashboard,
 * adapted to GeoTrust UX conventions and wired into the GeoTrust map.
 *
 * Implements the UI surface documented in
 * `GEOLINK_GEOTRUST_UI_INTEGRATION.md`:
 *   • Object list with filters (object_type / owner_wallet / "mine only").
 *   • Quick-add templates (Building / Floor / Zone / Event / Parcel /
 *     Trigger) that pre-fill object_type + sensible defaults.
 *   • Editor with map-driven geometry input:
 *       – Draw polygon / point / line via mapbox-gl-draw on the live map.
 *       – Import footprint + heights from existing Mapbox 3D buildings.
 *       – "Use my location" → Point geometry.
 *       – Inline geometry JSON for power users.
 *   • Execution-rule dropdown with auto-fill of linked contract +
 *     function (sourced from `GET /api/contracts/rules`).
 *   • Provider preview (Mapbox / MapLibre / MapTiler / Generic) showing
 *     the canonical FeatureCollection + suggested style block.
 *   • Interaction tester (`POST /admin/.../test-interaction`) with a
 *     "use one of my agents" picker so admins can verify decisions
 *     against the agent_pks they actually push positions for.
 *
 * Authorization model: the GeoLink wallet-provider key (`X-API-Key`) is
 * resolved from `spatialEnv.ts`. GeoLink defaults `owner_wallet` to the
 * key's resolved `public_key` when omitted, so the simplest create call
 * is `{ name, geometry }`. All writes preserve that default unless the
 * admin explicitly enters an owner.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import './SpatialObjectsAdminOverlay.css';

import { parseVectorFile, VECTOR_IMPORT_ACCEPT } from '../../services/spatial/vectorImport';

import {
  geoLinkSpatialObjectsAdminClient,
  type SpatialObjectAdminListQuery,
  type SpatialObjectAdminRow,
} from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import type {
  ContractExecutionRuleSummary,
  SpatialObjectAdminCreateBody,
  SpatialObjectInteractResponse,
  SpatialObjectType,
  SpatialObjectVisibility,
  SpatialObjectsRenderEnvelope,
  SpatialRenderProvider,
} from '../../services/spatial/geoLinkTypes';
import { metadataOrganizationBridgeIsSafe } from '../../services/spatial/geoLinkCapabilities';
import { listAgents, type CachedAgentRecord } from '../../services/agentService';
import { claimMapClicks, releaseMapClicks } from './mapClickClaim';
import SpatialPreviewMap from './SpatialPreviewMap';
import SpatialPresenceSections, { type StoredFlagIntent } from './SpatialPresenceSections';
import SpatialObjectSimulator from './SpatialObjectSimulator';
import {
  attributionFor,
  fetchOsmBuildingParts,
  mergeStructures,
  OSM_ATTRIBUTION,
  OSM_LICENSE_URL,
  type GeometrySource,
  type HeightProvenance,
  type OsmStructureOption,
} from '../../utils/osmBuildingFetcher';
import { scheduleSpatialOccasion } from '../../services/spatial/spatialPresenceClient';
import { flagImgProps } from '../../utils/countryVaultUtils';
import {
  OBJECT_TYPES,
  PARENT_TYPES_FOR,
  buildTree,
  countryOptions,
  decorateRows,
  defaultChildType,
  filterAndSortRows,
  formatDistance,
  geometryBounds,
  isUuid,
  nextFloorNumber,
  objectTypeLabel,
  parentCandidates,
  parentRequirement,
  pickRowAt,
  serverSortFor,
  type DecoratedRow,
  type ListBounds,
  type LngLat,
  type ResolveCountry,
  type SpatialListFilters,
  type SpatialSortKey,
} from './spatialAdminList';
import {
  getOrgConfig,
  getOrganization,
  listOrganizations,
  isOrgId,
  type Organization,
} from '../../v2/services/organizationService';
import type { SpatialSigner } from '../../v2/services/spatialRegistryService';
import { SpatialObjectOrganizationPanel } from '../spatial-bindings/SpatialObjectOrganizationPanel';
import { AuthorityPanel } from '../authority/AuthorityPanel';
import { stripDerivedPresentation } from './derivedPresentation';
import {
  useObjectAuthorization,
  useObjectAuthorizations,
  type AuthorizationState,
  type ObjectAuthorizationMapState,
} from '../../hooks/useAuthorization';
import {
  explain,
  legacyCreatorMayAct,
  type ObjectAuthorization,
} from '../../v2/services/authorizationModel';

type InteractDecision = 'allowed' | 'denied';

// ─── Constants & helpers ──────────────────────────────────────────────

const MAPBOX_BUILDING_LAYERS = [
  '3d-buildings',
  'building',
  'building-extrusion',
  'building-3d',
];

/** Temporary live-map preview of an imported building (cleared on next import / close). */
const IMPORT_PREVIEW_SOURCE = 'gl-import-building-preview';
const IMPORT_PREVIEW_FILL = 'gl-import-building-preview-fill';
const IMPORT_PREVIEW_EXTRUSION = 'gl-import-building-preview-extrusion';

// Quick templates mirror §3.5 of the UI integration guide.
const QUICK_TEMPLATES: {
  id: string;
  label: string;
  icon: string;
  defaults: Partial<SpatialObjectAdminCreateBody>;
}[] = [
  { id: 'building', label: 'Building', icon: '🏢', defaults: { object_type: 'building',  visibility: 'public', height_meters: 30, base_height_meters: 0 } },
  { id: 'floor',    label: 'Floor',    icon: '🪜',  defaults: { object_type: 'floor',     height_meters: 4,  base_height_meters: 12, floor_number: 1 } },
  { id: 'zone',     label: 'Zone',     icon: '📐', defaults: { object_type: 'zone',      visibility: 'public' } },
  { id: 'event',    label: 'Event',    icon: '🎉', defaults: { object_type: 'event_object', visibility: 'public' } },
  { id: 'parcel',   label: 'Parcel',   icon: '🏞️',  defaults: { object_type: 'real_estate' } },
  { id: 'trigger',  label: 'Trigger',  icon: '⚡', defaults: { object_type: 'contract_execution_object' } },
];

const PROVIDERS: SpatialRenderProvider[] = ['mapbox', 'maptiler', 'maplibre', 'generic'];

/**
 * Per-tier metadata for a multi-stepped building (Burj Khalifa,
 * Empire State, …). Each tier corresponds to one polygon in the
 * spatial object's MultiPolygon geometry — `polygon_index` indexes
 * into `geometry.coordinates`. Heights are stored in metres and use
 * the same convention as the spatial object's top-level
 * `base_height_meters` / `height_meters`: ground level = 0, both
 * values inclusive of the building's ground footprint.
 *
 * The renderer's tier-expansion path (in SpatialObjectsMapLayer)
 * splits a tiered MultiPolygon Feature into one Feature per tier so
 * each polygon extrudes between its own (min_height, height) range.
 * That gives Burj Khalifa its visible setbacks instead of a single
 * 828m extrusion of the wide ground footprint.
 */
export interface BuildingTier {
  polygon_index: number;
  min_height: number;
  height:     number;
}

/** Build a FeatureCollection for per-tier preview extrusions. */
function importPreviewFeatures(
  polygons: number[][][][],
  tiers: BuildingTier[],
  heightMeters: number,
  baseHeightMeters: number,
): GeoJSON.FeatureCollection {
  // One part previews from its own extent too: a section that starts at 55m
  // would otherwise be shown sitting on the ground.
  if (tiers.length > 0) {
    return {
      type: 'FeatureCollection',
      features: tiers.map((t, i) => ({
        type: 'Feature' as const,
        properties: {
          heightMeters: t.height,
          baseHeightMeters: t.min_height,
        },
        geometry: {
          type: 'Polygon' as const,
          coordinates: polygons[t.polygon_index] ?? polygons[i] ?? [],
        },
      })).filter((f) => (f.geometry.coordinates as unknown[]).length > 0),
    };
  }
  const geom =
    polygons.length === 1
      ? { type: 'Polygon' as const, coordinates: polygons[0] }
      : { type: 'MultiPolygon' as const, coordinates: polygons };
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { heightMeters, baseHeightMeters },
      geometry: geom,
    }],
  };
}

function clearImportPreview(map: mapboxgl.Map | null): void {
  if (!map) return;
  try {
    if (map.getLayer(IMPORT_PREVIEW_EXTRUSION)) map.removeLayer(IMPORT_PREVIEW_EXTRUSION);
    if (map.getLayer(IMPORT_PREVIEW_FILL)) map.removeLayer(IMPORT_PREVIEW_FILL);
    if (map.getSource(IMPORT_PREVIEW_SOURCE)) map.removeSource(IMPORT_PREVIEW_SOURCE);
  } catch { /* style mid-swap */ }
}

function showImportPreview(
  map: mapboxgl.Map | null,
  polygons: number[][][][],
  tiers: BuildingTier[],
  heightMeters: number,
  baseHeightMeters: number,
): void {
  if (!map || polygons.length === 0) return;
  clearImportPreview(map);
  const data = importPreviewFeatures(polygons, tiers, heightMeters, baseHeightMeters);
  try {
    map.addSource(IMPORT_PREVIEW_SOURCE, { type: 'geojson', data });
    map.addLayer({
      id: IMPORT_PREVIEW_FILL,
      type: 'fill',
      source: IMPORT_PREVIEW_SOURCE,
      paint: {
        'fill-color': '#38bdf8',
        'fill-opacity': 0.15,
        'fill-outline-color': '#0ea5e9',
      },
    });
    map.addLayer({
      id: IMPORT_PREVIEW_EXTRUSION,
      type: 'fill-extrusion',
      source: IMPORT_PREVIEW_SOURCE,
      paint: {
        'fill-extrusion-color': '#38bdf8',
        'fill-extrusion-height': ['coalesce', ['get', 'heightMeters'], 1],
        'fill-extrusion-base': ['coalesce', ['get', 'baseHeightMeters'], 0],
        'fill-extrusion-opacity': 0.65,
      },
    });
  } catch { /* style not ready */ }
}

/** Fly the camera to an imported MultiPolygon / Polygon footprint. */
function flyToImportedPolygons(map: mapboxgl.Map | null, polygons: number[][][][]): void {
  if (!map || polygons.length === 0) return;
  let sumLng = 0;
  let sumLat = 0;
  let n = 0;
  for (const poly of polygons) {
    const ring = poly[0];
    if (!ring) continue;
    for (const pt of ring) {
      if (typeof pt[0] === 'number' && typeof pt[1] === 'number') {
        sumLng += pt[0];
        sumLat += pt[1];
        n++;
      }
    }
  }
  if (n === 0) return;
  try {
    map.flyTo({
      center: [sumLng / n, sumLat / n],
      zoom: Math.max(map.getZoom(), 16),
      pitch: Math.max(map.getPitch(), 50),
      duration: 900,
    });
  } catch { /* ignore */ }
}

interface FormState {
  id?: string;
  object_type: SpatialObjectType;
  name: string;
  description: string;
  owner_wallet: string;
  /** Wallet that originally created the object (preserved across edits so
   *  the per-creator edit/delete guard survives updates). */
  created_by_wallet: string;
  visibility: SpatialObjectVisibility;
  geometry: string;       // JSON text — parsed on save
  base_height_meters: string;
  height_meters: string;
  floor_number: string;
  parent_object_id: string;
  color_key: string;
  interaction_types: string;       // CSV
  linked_execution_rule_id: string;
  linked_contract_address: string;
  linked_contract_function: string;
  /**
   * Per-polygon tier metadata captured at import time. Empty `[]`
   * when the building is a single-tier extrusion (we then fall back
   * to base_height_meters / height_meters at render time). Persisted
   * as `metadata.tiers` on the spatial object.
   */
  tiers: BuildingTier[];
  /**
   * Where this geometry came from, when an import produced it.
   *
   * Persisted as `metadata.geometry_provenance`. Kept deliberately small:
   * enough to answer "is this surveyed data or our guess, and which OSM
   * objects did it come from", and nothing more. The raw Overpass
   * response is not stored — it is large, it goes stale, and every
   * question worth asking of it is answered by the ids.
   */
  geometry_provenance: {
    source: GeometrySource;
    sourceIds: number[];
    importedAt: string;
    heightProvenance: HeightProvenance;
  } | null;
  /**
   * Owning organization, as a 64-hex `geo-org-registry` id. Persisted to
   * `metadata.organization_id` rather than a column because GeoLink has
   * no `organizations` table yet — see ADR-09 and Part 2 of
   * docs/geolink-spatial-admin-and-organizations.md. The chain stays
   * authoritative for what the organization *is*; this is only the link.
   */
  organization_id: string;
  /**
   * The occasion this object hosts, persisted as `metadata.event`.
   *
   * Not restricted to `event_object`: a National Day belongs to the
   * tower it lights up, not to a separate marker floating beside it.
   * Times are held as `datetime-local` wall-clock strings and converted
   * to instants on save.
   */
  event_title: string;
  event_starts_at: string;
  event_ends_at: string;
  event_venue: string;
  event_ticket_contract: string;
  /**
   * Metadata keys this form does not model, carried through untouched.
   *
   * The body is rebuilt from the form on every save, so without this any
   * key the form has no field for — a surface flag render hint, anything
   * a future GeoLink adds — would be silently dropped by an unrelated
   * edit to the object's name.
   */
  metadata_extra: Record<string, unknown>;
}

/** Keys `bodyFromForm` writes itself; everything else is carried through. */
const FORM_OWNED_METADATA_KEYS = [
  'color_key', 'interaction_types', 'createdByWallet', 'organization_id',
  'tiers', 'event',
];

/** ISO instant → the wall-clock value a `datetime-local` input wants. */
function isoToLocalInput(iso: unknown): string {
  if (typeof iso !== 'string' || !iso.trim()) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` wall clock → ISO instant, or '' when unparseable. */
function localInputToIso(value: string): string {
  if (!value.trim()) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

const EMPTY_FORM: FormState = {
  object_type: 'building',
  geometry_provenance: null,
  name: '',
  description: '',
  owner_wallet: '',
  created_by_wallet: '',
  visibility: 'public',
  geometry: '',
  base_height_meters: '0',
  height_meters: '30',
  floor_number: '',
  parent_object_id: '',
  color_key: '#4A90E2',
  interaction_types: 'tap,info',
  linked_execution_rule_id: '',
  linked_contract_address: '',
  linked_contract_function: '',
  tiers: [],
  organization_id: '',
  event_title: '',
  event_starts_at: '',
  event_ends_at: '',
  event_venue: '',
  event_ticket_contract: '',
  metadata_extra: {},
};

/**
 * GeoLink sometimes stores a MultiPolygon as a GeometryCollection of
 * Polygons. Tier `polygon_index` values are ordered against a flat
 * MultiPolygon coordinate list — coerce so edit + re-save don't scramble
 * Atlantis / Burj setbacks.
 */
function coercePolygonsToMultiPolygon(geometry: unknown): unknown {
  if (!geometry || typeof geometry !== 'object') return geometry;
  const g = geometry as { type?: string; coordinates?: unknown; geometries?: unknown[] };
  if (g.type === 'MultiPolygon' || g.type === 'Polygon') return geometry;
  if (g.type !== 'GeometryCollection' || !Array.isArray(g.geometries)) return geometry;
  const parts: unknown[] = [];
  const walk = (geom: unknown): void => {
    if (!geom || typeof geom !== 'object') return;
    const x = geom as { type?: string; coordinates?: unknown; geometries?: unknown[] };
    if (x.type === 'Polygon' && Array.isArray(x.coordinates)) parts.push(x.coordinates);
    else if (x.type === 'MultiPolygon' && Array.isArray(x.coordinates)) {
      for (const p of x.coordinates as unknown[]) parts.push(p);
    } else if (x.type === 'GeometryCollection' && Array.isArray(x.geometries)) {
      for (const c of x.geometries) walk(c);
    }
  };
  for (const child of g.geometries) walk(child);
  if (parts.length === 0) return geometry;
  if (parts.length === 1) return { type: 'Polygon', coordinates: parts[0] };
  return { type: 'MultiPolygon', coordinates: parts };
}

/** Push admin-row tiers into the live map layer (+ localStorage via upsert). */
function seedLiveMapTiersFromRow(row: SpatialObjectAdminRow): void {
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  const raw = meta.tiers;
  if (!Array.isArray(raw) || raw.length === 0 || !row.geometry) return;
  const tiers: BuildingTier[] = [];
  for (const t of raw) {
    if (!t || typeof t !== 'object') continue;
    const r = t as Record<string, unknown>;
    const idx = Number(r.polygon_index);
    const mn = Number(r.min_height);
    const mx = Number(r.height);
    if (!Number.isFinite(idx) || !Number.isFinite(mn) || !Number.isFinite(mx)) continue;
    tiers.push({ polygon_index: idx, min_height: mn, height: mx });
  }
  if (tiers.length === 0) return;
  const geometry = coercePolygonsToMultiPolygon(row.geometry) as GeoJSON.Geometry;
  // GeoLink can collapse overlapping setback footprints on save (a 45-part
  // Atlantis MultiPolygon comes back with 11 polygons) while metadata.tiers
  // still references all 45 indices. Such a row can never render its tiers —
  // dispatching it would only poison the live map's tier/geometry cache and
  // clobber the good import pair, so skip it.
  const partCount =
    geometry.type === 'MultiPolygon' ? (geometry as GeoJSON.MultiPolygon).coordinates.length
    : geometry.type === 'Polygon' ? 1
    : 0;
  const maxIdx = tiers.reduce((m, t) => Math.max(m, t.polygon_index), 0);
  if (maxIdx >= partCount) return;
  window.dispatchEvent(new CustomEvent('geolink:spatial-object-upsert', {
    detail: {
      id: row.id,
      geometry,
      heightMeters: row.heightMeters ?? undefined,
      baseHeightMeters: row.baseHeightMeters ?? undefined,
      tiers,
      name: row.name ?? undefined,
      objectType: row.objectType,
      colorKey: typeof meta.color_key === 'string' ? meta.color_key : undefined,
    },
  }));
}

/**
 * Read a stored provenance blob back, or `null` if it is absent or
 * unreadable. An object saved before provenance existed simply has none,
 * which is different from having been drawn by hand — neither claim is
 * made here.
 */
function parseGeometryProvenance(raw: unknown): FormState['geometry_provenance'] {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const source = r.source;
  if (source !== 'openstreetmap'
    && source !== 'osm-buildings-tiles'
    && source !== 'mapbox-3d-tiles') return null;
  const hp = r.heightProvenance;
  return {
    source,
    sourceIds: Array.isArray(r.sourceIds)
      ? r.sourceIds.filter((n): n is number => typeof n === 'number')
      : [],
    importedAt: typeof r.importedAt === 'string' ? r.importedAt : '',
    heightProvenance:
      hp === 'measured' || hp === 'inferred' ? hp : 'unknown',
  };
}

const formFromRow = (row: SpatialObjectAdminRow): FormState => ({
  id: row.id,
  object_type:        row.objectType ?? 'building',
  // Read back so an edit-and-save does not erase an existing record's
  // provenance, and so the editor can say where the geometry came from.
  geometry_provenance: parseGeometryProvenance(
    (row.metadata as { geometry_provenance?: unknown } | null | undefined)
      ?.geometry_provenance,
  ),
  name:               row.name ?? '',
  description:        row.description ?? '',
  owner_wallet:       row.ownerWallet ?? '',
  created_by_wallet:  ((row.metadata as { createdByWallet?: unknown } | null | undefined)?.createdByWallet as string | undefined)
                        ?? row.ownerWallet ?? '',
  visibility:         row.visibility ?? 'public',
  geometry:           row.geometry
    ? JSON.stringify(coercePolygonsToMultiPolygon(row.geometry), null, 2)
    : '',
  base_height_meters: String(row.baseHeightMeters ?? 0),
  // Prefer peak from metadata.tiers when the list height is a stale
  // summary (or a single setback) so the form shows 193m for Atlantis.
  height_meters: (() => {
    const listed = Number(row.heightMeters);
    const raw = (row.metadata as { tiers?: unknown } | null | undefined)?.tiers;
    let peak = Number.isFinite(listed) ? listed : NaN;
    if (Array.isArray(raw)) {
      for (const t of raw) {
        if (!t || typeof t !== 'object') continue;
        const h = Number((t as { height?: unknown }).height);
        if (Number.isFinite(h)) peak = Number.isFinite(peak) ? Math.max(peak, h) : h;
      }
    }
    return Number.isFinite(peak) ? String(peak) : '';
  })(),
  floor_number:       row.floorNumber == null ? '' : String(row.floorNumber),
  parent_object_id:   row.parentObjectId ?? '',
  color_key: String(
    (row.metadata as { color_key?: unknown } | null | undefined)?.color_key ?? '#4A90E2',
  ),
  interaction_types: Array.isArray((row.metadata as { interaction_types?: unknown } | null | undefined)?.interaction_types)
    ? ((row.metadata as { interaction_types?: string[] })!.interaction_types as string[]).join(',')
    : 'tap,info',
  // Restore any per-polygon tier metadata previously captured at
  // import time. Stored as `metadata.tiers` on the spatial object.
  // Defensive parse: ignore malformed entries so a single bad value
  // doesn't break the form for the whole record.
  tiers: (() => {
    const raw = (row.metadata as { tiers?: unknown } | null | undefined)?.tiers;
    if (!Array.isArray(raw)) return [];
    const out: BuildingTier[] = [];
    for (const t of raw) {
      if (!t || typeof t !== 'object') continue;
      const r = t as Record<string, unknown>;
      const idx = Number(r.polygon_index);
      const mn  = Number(r.min_height);
      const mx  = Number(r.height);
      if (!Number.isFinite(idx) || !Number.isFinite(mn) || !Number.isFinite(mx)) continue;
      out.push({ polygon_index: idx, min_height: mn, height: mx });
    }
    return out;
  })(),
  linked_execution_rule_id: row.linkedExecutionRuleId == null ? '' : String(row.linkedExecutionRuleId),
  linked_contract_address:  row.linkedContractAddress ?? '',
  linked_contract_function: row.linkedContractFunction ?? '',
  organization_id: (() => {
    const raw = (row.metadata as { organization_id?: unknown } | null | undefined)?.organization_id;
    return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  })(),
  event_title: eventFieldOf(row, 'title'),
  event_starts_at: isoToLocalInput(eventFieldOf(row, 'startsAt')),
  event_ends_at: isoToLocalInput(eventFieldOf(row, 'endsAt')),
  event_venue: eventFieldOf(row, 'venue'),
  event_ticket_contract: eventFieldOf(row, 'ticketContract'),
  metadata_extra: (() => {
    const meta = (row.metadata ?? {}) as Record<string, unknown>;
    const extra: Record<string, unknown> = {};
    Object.keys(meta).forEach((k) => {
      if (!FORM_OWNED_METADATA_KEYS.includes(k)) extra[k] = meta[k];
    });
    return extra;
  })(),
});

/** One string field of `metadata.event`, or '' when it is absent. */
function eventFieldOf(row: SpatialObjectAdminRow, key: string): string {
  const ev = (row.metadata as { event?: unknown } | null | undefined)?.event;
  if (!ev || typeof ev !== 'object') return '';
  const v = (ev as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : '';
}

/**
 * Validate `form.parent_object_id` against the child type's
 * requirement. Returns the normalised id (or null) and, when the link
 * is legal but questionable, a warning for the operator.
 *
 * GeoLink rejects a non-UUID `parent_object_id` server-side with a
 * generic 400, so catching the shape here gives a far better message.
 */
function resolveParentLink(
  form: FormState,
  knownIds?: Set<string>,
): { parentId: string | null; warning: string | null } {
  const parentId = form.parent_object_id.trim();
  const requirement = parentRequirement(form.object_type);
  const typeLabel = objectTypeLabel(form.object_type).toLowerCase();

  if (!parentId) {
    if (requirement === 'required') {
      const allowed = (PARENT_TYPES_FOR[form.object_type] ?? []).map(objectTypeLabel).join(' or ');
      throw new Error(
        `A ${typeLabel} must belong to a parent object${allowed ? ` (${allowed})` : ''}. ` +
        `Pick one in the Parent object field.`,
      );
    }
    return {
      parentId: null,
      warning: requirement === 'recommended'
        ? `Saving this ${typeLabel} without a parent — it will appear as a standalone object.`
        : null,
    };
  }

  if (!isUuid(parentId)) {
    throw new Error('Parent object id must be a UUID. Pick a parent from the list instead of typing one.');
  }
  if (knownIds && !knownIds.has(parentId)) {
    throw new Error(
      `Parent object ${parentId.slice(0, 8)}… is not in the loaded set. ` +
      `Refresh the list, or clear the type / owner filter that is hiding it.`,
    );
  }
  if (form.id && parentId === form.id) {
    throw new Error('An object cannot be its own parent.');
  }
  return { parentId, warning: null };
}

const buildCreateBody = (
  form: FormState,
  creatorWallet?: string | null,
  knownIds?: Set<string>,
  /**
   * Whether GeoLink's `metadata.organization_id` → column promotion is
   * guarded. See the call site in `saveForm` and the comment on the
   * field below.
   */
  bridgeSafe = false,
): SpatialObjectAdminCreateBody => {
  const { parentId } = resolveParentLink(form, knownIds);
  let geometry: SpatialObjectAdminCreateBody['geometry'];
  try {
    const parsed = coercePolygonsToMultiPolygon(JSON.parse(form.geometry || '{}'));
    if (!parsed || typeof parsed !== 'object' || !('type' in parsed)) {
      throw new Error('Geometry must be a valid GeoJSON object with `type`.');
    }
    const g = parsed as { type: string; coordinates?: unknown; geometries?: unknown };
    // After coerce, Polygon/MultiPolygon have coordinates; reject leftovers.
    if ((g.type === 'Polygon' || g.type === 'MultiPolygon') && !('coordinates' in g)) {
      throw new Error('Geometry must include `coordinates`.');
    }
    if (g.type !== 'Polygon' && g.type !== 'MultiPolygon' && g.type !== 'Point' && g.type !== 'LineString') {
      throw new Error(`Unsupported geometry type "${g.type}" — use Polygon or MultiPolygon.`);
    }
    geometry = parsed as SpatialObjectAdminCreateBody['geometry'];
  } catch (e) {
    throw new Error(`Invalid geometry: ${(e as Error).message}`);
  }

  const interaction_types = form.interaction_types
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

  const ruleId   = form.linked_execution_rule_id.trim();
  const ruleNum  = ruleId === '' ? null : Number(ruleId);
  const ruleVal  = ruleNum != null && Number.isFinite(ruleNum) ? ruleNum : (ruleId || null);

  // On a fresh create we stamp the connected wallet; on edit we preserve
  // the original creator carried in the form so updates don't orphan it.
  const creator = (creatorWallet ?? '').trim() || form.created_by_wallet.trim();

  const body: SpatialObjectAdminCreateBody = {
    object_type: form.object_type,
    name: form.name.trim(),
    description: form.description.trim() || null,
    owner_wallet: form.owner_wallet.trim() || null,
    visibility: form.visibility,
    geometry,
    base_height_meters: form.base_height_meters === '' ? 0 : Number(form.base_height_meters),
    height_meters: (() => {
      const listed = form.height_meters === '' ? 0 : Number(form.height_meters);
      if (form.tiers.length === 0) return Number.isFinite(listed) ? listed : 0;
      let peak = Number.isFinite(listed) ? listed : 0;
      for (const t of form.tiers) {
        if (Number.isFinite(t.height) && t.height > peak) peak = t.height;
      }
      return peak;
    })(),
    floor_number:       form.floor_number       === '' ? null : Number(form.floor_number),
    parent_object_id:   parentId,
    metadata: {
      // Keys the form does not model, first so an explicit field always
      // wins. Without this, editing a name would drop the flag render
      // hint the presence editor wrote.
      //
      // Stripped of anything the map derives about *now*. Those are
      // computed from stored data for painting and must not travel back
      // as stored data themselves: saved once, a derived state stops
      // updating and keeps asserting a moment that has passed.
      ...stripDerivedPresentation(form.metadata_extra),
      color_key: form.color_key.trim() || '#4A90E2',
      interaction_types: interaction_types.length > 0 ? interaction_types : ['tap'],
      // Stamp the creating wallet so the UI can gate edit/delete to the
      // owner (others can view but not modify another wallet's objects).
      ...(creator ? { createdByWallet: creator } : {}),
      // Owning organization. Absent rather than null when unset, so
      // existing objects are not rewritten with an empty binding.
      //
      // Withheld unless GeoLink reports the promotion guarded. A build
      // carrying migrations 042+ under code predating the guard
      // promotes this key straight into a foreign key with no fallback,
      // so one unmirrored organization would reject the ENTIRE object
      // write — geometry and tiers included — over a field the operator
      // never typed. Guarded builds degrade the column to NULL and keep
      // the metadata copy, which the next write promotes.
      ...(bridgeSafe && form.organization_id.trim()
        ? { organization_id: form.organization_id.trim().toLowerCase() }
        : {}),
      // Per-polygon extrusion extents. Emitted for a single part too: the
      // simpler height_meters / base_height_meters pair describes the whole
      // object, so dropping a lone entry silently flattened a section that
      // runs from 55m to 85m onto the object's own extent.
      ...(form.tiers.length > 0 ? { tiers: form.tiers } : {}),
      // Where the geometry came from, when something other than a hand
      // drawing produced it.
      ...(form.geometry_provenance
        ? { geometry_provenance: form.geometry_provenance }
        : {}),
      // An occasion needs a name to be one; a title-less block of times
      // would light up the display path with an empty headline.
      ...(form.event_title.trim()
        ? {
          event: {
            title: form.event_title.trim(),
            ...(localInputToIso(form.event_starts_at)
              ? { startsAt: localInputToIso(form.event_starts_at) } : {}),
            ...(localInputToIso(form.event_ends_at)
              ? { endsAt: localInputToIso(form.event_ends_at) } : {}),
            ...(form.event_venue.trim() ? { venue: form.event_venue.trim() } : {}),
            ...(form.event_ticket_contract.trim()
              ? { ticketContract: form.event_ticket_contract.trim() } : {}),
          },
        }
        : {}),
    },
    linked_execution_rule_id: ruleVal,
    linked_contract_address:  form.linked_contract_address.trim() || null,
    linked_contract_function: form.linked_contract_function.trim() || null,
  };
  if (!body.name) throw new Error('Name is required.');
  return body;
};

/**
 * The wallet that created an object, for per-creator edit/delete gating.
 * We stamp `metadata.createdByWallet` at create time (the canonical
 * source) and fall back to `ownerWallet` for objects made before that
 * field existed. Returns '' when neither is known (legacy/unowned).
 */
const rowCreatorWallet = (row: SpatialObjectAdminRow): string => {
  const meta = (row.metadata ?? null) as { createdByWallet?: unknown } | null;
  const stamped = typeof meta?.createdByWallet === 'string' ? meta.createdByWallet.trim() : '';
  if (stamped) return stamped;
  return (row.ownerWallet ?? '').trim();
};

/**
 * Whether the current operator may edit or delete a row.
 *
 * Asks the authorization service, and only falls back to stored provenance
 * for an object the chain has never heard of. Two things this deliberately
 * does not do, both of them things its predecessor did:
 *
 * It does not let provenance override the chain. Once an object is
 * registered and bound, whoever created the GeoLink row years ago has no
 * say — that is the whole point of the refactor, since ownership can move
 * and history cannot be un-written.
 *
 * It does not treat an unknown creator as permission. The previous version
 * returned true when no creator was recorded, which made every object
 * predating that field editable by any connected wallet.
 *
 * `isAdmin` is still honoured as a fast path, but it is now corroborated:
 * the authorization response carries the country-scoped permissions for the
 * same wallet, so a stale `isAdmin` cannot outlive the contract's answer for
 * a registered object.
 */
const creatorMayEdit = (
  creatorWallet: string,
  auth: ObjectAuthorization | null,
  walletAddress: string | null,
  isAdmin: boolean,
): boolean => {
  if (!walletAddress) return false;

  if (auth) {
    // Editing a GeoLink row is a change to what the object is, so either
    // route to authority is enough: administrative (country scope) or
    // operational (through the owning organization).
    if (auth.canManageObject || auth.canManageBinding || auth.canSuspendObject) return true;
    // A registered object is decided on chain and nowhere else.
    if (auth.registered) return false;
  }

  if (isAdmin) return true;
  return legacyCreatorMayAct(creatorWallet, walletAddress, !!auth?.registered);
};

const rowMayBeEdited = (
  row: SpatialObjectAdminRow,
  auth: ObjectAuthorization | null,
  walletAddress: string | null,
  isAdmin: boolean,
): boolean => creatorMayEdit(rowCreatorWallet(row), auth, walletAddress, isAdmin);

/** Why `rowMayBeEdited` said no, for a tooltip on the disabled control. */
const rowEditRefusal = (
  auth: ObjectAuthorization | null,
  walletAddress: string | null,
): string => {
  if (!walletAddress) return 'Connect a wallet to enable';
  if (!auth) return 'Authorization for this object has not been resolved yet.';
  if (auth.registered) {
    return explain(auth, 'canManageObject')
      || 'This wallet has no authority over this object.';
  }
  return 'This object is not registered on chain, and this wallet is not the '
    + 'wallet recorded as its creator.';
};

// ─── Component ────────────────────────────────────────────────────────

/**
 * Hand-off shape used by the country tooltip to ask the floating panel
 * to do one of: edit a specific object, start a new object from a
 * template centred on a country, or just open the list filtered to a
 * bbox. The overlay consumes the params on open and then calls
 * `onParamsConsumed` so the parent can clear them.
 */
export interface SpatialAdminOpenParams {
  objectId?: string;
  template?: 'building' | 'floor' | 'zone' | 'event' | 'parcel' | 'trigger';
  /** Set Point geometry to this lng/lat when starting a new object. */
  center?: { lng: number; lat: number };
  /** When true, fly the live map to {@link center} after applying it. Without
   *  this the operator's existing camera view is preserved (preferred when
   *  they were already zoomed in on the area they want the object in). */
  flyToCenter?: boolean;
  /** Used for "Manage all" — pre-filter the list when neither objectId nor template applies. */
  bbox?: [number, number, number, number];
}

interface SpatialObjectsAdminOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  /** Live Mapbox map. Used for drawing geometry, importing 3D buildings,
   *  and flying to addresses / "Use my location". */
  map: mapboxgl.Map | null;
  walletAddress: string | null;
  /** Optional GeoTrust player position fallback for "Use my location". */
  playerLocation?: { latitude: number; longitude: number } | null;
  /** Mapbox token for the geocoder (re-uses the one already in env). */
  mapboxToken?: string | null;
  /** Pre-load the panel into a specific state when it opens. Cleared via `onParamsConsumed`. */
  openParams?: SpatialAdminOpenParams | null;
  /** Called once after the panel has applied `openParams`. */
  onParamsConsumed?: () => void;
  /**
   * How the panel should render:
   *   - "floating" (default) — small draggable card pinned to the top-right
   *     corner. Used when the panel is invoked from the country tooltip's
   *     spatial section (we want the live map visible alongside).
   *   - "centered" — larger modal-style sheet centred on the viewport with
   *     a backdrop. Used when invoked from the hamburger menu, where the
   *     operator wants to focus on managing objects rather than the map.
   * Drag, minimise and per-position localStorage persistence apply only
   * to the floating mode.
   */
  openMode?: 'floating' | 'centered';
  /** When true the operator is an admin and may edit/delete any object,
   *  bypassing the per-creator ownership guard. */
  isAdmin?: boolean;
  /**
   * App's synchronous lng/lat → country resolver. GeoLink does not
   * return `country_code` on admin rows, so we derive the country
   * column and filter locally from each row's centroid.
   */
  resolveCountry?: ResolveCountry | null;
  /**
   * Wallet used to sign spatial *registry* writes.
   *
   * Object CRUD here goes to GeoLink over REST with an API key and needs
   * no signature. The on-chain organization binding is the exception: it
   * is a contract call the operator authorizes themselves, so it needs a
   * real signer. Absent, the binding panel renders read-only.
   */
  signer?: SpatialSigner | null;
  networkPassphrase?: string;
}

type Tab = 'objects' | 'editor' | 'preview' | 'tester';

export const SpatialObjectsAdminOverlay: React.FC<SpatialObjectsAdminOverlayProps> = ({
  isOpen,
  onClose,
  map,
  walletAddress,
  playerLocation,
  mapboxToken,
  openParams,
  onParamsConsumed,
  openMode = 'floating',
  isAdmin = false,
  resolveCountry = null,
  signer = null,
  networkPassphrase,
}) => {
  const [tab, setTab] = useState<Tab>('objects');
  const [rows, setRows] = useState<SpatialObjectAdminRow[]>([]);
  // Mirror of `rows` in a ref so the openParams effect can look up an
  // object by id without having `rows` in its dep array (we don't want
  // the effect to re-run every time the list refreshes).
  const rowsRef = useRef<SpatialObjectAdminRow[]>([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  // Filters
  const [filterType, setFilterType]   = useState<SpatialObjectType | ''>('');
  const [filterOwner, setFilterOwner] = useState('');
  const [mineOnly, setMineOnly]       = useState(false);
  const [listSearch, setListSearch]   = useState('');
  // Settled search term. Filtering reads this rather than `listSearch`
  // so an overflowed result set issues one query per pause instead of
  // one per keystroke.
  const [searchTerm, setSearchTerm]   = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [inViewOnly, setInViewOnly]   = useState(false);
  const [sortKey, setSortKey]         = useState<SpatialSortKey>('view');

  // Live map viewport, mirrored into state so the list re-scopes as the
  // operator pans. Normally this never refetches — scoping happens in
  // memory over rows we already hold, which keeps panning instant and
  // keeps off-screen parents available to the picker and the tree. The
  // one exception is `serverScoped` (see refreshRows).
  const [viewport, setViewport] = useState<{ bounds: ListBounds | null; center: LngLat | null }>({
    bounds: null,
    center: null,
  });
  // Read by refreshRows, which must not re-identify on every pan.
  const viewportRef = useRef(viewport);
  useEffect(() => { viewportRef.current = viewport; }, [viewport]);

  /** Unpaged size of the filtered set. Null on GeoLink builds without `total`. */
  const [totalCount, setTotalCount] = useState<number | null>(null);
  /**
   * True when the loaded page came back viewport-scoped because the full
   * result set overflowed the page limit. Filtering then belongs to
   * PostGIS, not to us.
   */
  const [serverScoped, setServerScoped] = useState(false);

  // Editor
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  // Mirror for native map handlers, which outlive the render that
  // registered them and must not read a stale form.
  const formRef = useRef<FormState>(EMPTY_FORM);
  useEffect(() => { formRef.current = form; }, [form]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * Contract-backed authority for the object in the editor.
   *
   * Held here rather than inside `EditorTab` because `saveForm` needs the
   * same answer the panel renders. Two components each resolving their own
   * would eventually disagree, and the one that disagrees quietly is the
   * one that writes.
   */
  const formAuthorization = useObjectAuthorization(
    (form.id ?? '').trim() || null,
    walletAddress,
  );
  // Mirrored so `saveForm` and `deleteRow` — both `useCallback`s that
  // outlive the render they were created in — cannot check a stale answer.
  const formAuthRef = useRef(formAuthorization);
  useEffect(() => { formAuthRef.current = formAuthorization; }, [formAuthorization]);

  /**
   * Authority for every row currently listed, in batches.
   *
   * The list is where the old creator comparison did the most damage: it
   * decided edit, delete and bulk-select for every row from a metadata
   * field. One batched request answers all of them from the chain instead.
   */
  const rowIds = useMemo(() => rows.map((r) => r.id).filter(Boolean), [rows]);
  const rowAuthorizations = useObjectAuthorizations(rowIds, walletAddress);
  const authForRowRef = useRef(rowAuthorizations.authFor);
  useEffect(() => { authForRowRef.current = rowAuthorizations.authFor; }, [rowAuthorizations.authFor]);

  // Rule auto-fill source
  const [rules, setRules] = useState<ContractExecutionRuleSummary[]>([]);

  // Agents (for the tester actor picker)
  const [agents, setAgents] = useState<CachedAgentRecord[]>([]);

  // Tester
  const [testerObjectId, setTesterObjectId]   = useState('');
  const [testerActor, setTesterActor]         = useState('');
  const [testerInteraction, setTesterInteraction] = useState('tap');
  const [testerResult, setTesterResult]       = useState<SpatialObjectInteractResponse | null>(null);

  // Preview
  const [previewProvider, setPreviewProvider] = useState<SpatialRenderProvider>('mapbox');
  const [previewEnvelope, setPreviewEnvelope] = useState<SpatialObjectsRenderEnvelope | null>(null);
  const [previewObjectId, setPreviewObjectId] = useState('');

  // Map-side draw state
  const drawRef         = useRef<MapboxDraw | null>(null);
  const importHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null);
  const drawCreateHandlerRef = useRef<(() => void) | null>(null);
  // AbortController for the in-flight Overpass request, if any. We
  // cancel it from teardownImport / on a new click so a stale OSM
  // result can never overwrite a fresher import (or fire after the
  // operator has switched modes).
  const importOsmAbortRef = useRef<AbortController | null>(null);

  // ─── Floating-window state (drag + minimise + persistence) ──────────
  // Sensible defaults: tucked under the navigation hamburger so the
  // panel never covers the menu trigger that opened it.
  const PANEL_DEFAULT_W = 460;
  const PANEL_DEFAULT_H = 640;
  const POS_KEY = 'geotrust:spatial-admin-panel:pos';
  const MIN_KEY = 'geotrust:spatial-admin-panel:minimized';

  const readStoredPos = useCallback((): { x: number; y: number } | null => {
    try {
      const raw = window.localStorage.getItem(POS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { x?: number; y?: number };
      if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null;
      return { x: parsed.x, y: parsed.y };
    } catch { return null; }
  }, []);

  const defaultPos = useCallback((): { x: number; y: number } => {
    if (typeof window === 'undefined') return { x: 16, y: 80 };
    const x = Math.max(8, window.innerWidth  - PANEL_DEFAULT_W - 16);
    const y = 80;
    return { x, y };
  }, []);

  const [pos, setPos]             = useState<{ x: number; y: number }>(() => readStoredPos() ?? defaultPos());
  const [minimized, setMinimized] = useState<boolean>(() => {
    try { return window.localStorage.getItem(MIN_KEY) === '1'; } catch { return false; }
  });
  const [dragging, setDragging]   = useState(false);

  /**
   * "Picking mode" — the panel is currently waiting for the operator to
   * click a building / vertex on the live map. While this is active the
   * full panel UI gets replaced by a thin top-of-screen status bar so
   * the map underneath is fully reachable. This is critical for the
   * centered modal mode (the backdrop + sheet otherwise cover the map
   * completely), and a useful hint in floating mode too.
   *
   * Lifecycle: set by `startImportFrom3DBuilding` / `startDraw`, cleared
   * by `teardownImport` / `teardownDraw`. Both teardowns also fire when
   * the panel closes mid-pick, so a stuck status bar can't survive a
   * close.
   */
  const [pickingMode, setPickingMode] = useState<'import-building' | 'draw' | 'parent' | null>(null);
  const parentPickHandlerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null);

  /**
   * Structures OSM offered for the last click, when it offered more than
   * one. A landmark is often several separate top-level ways with nothing
   * in OSM to say they belong together, so which of them the operator
   * means is a question only the operator can answer.
   *
   * `null` whenever there is nothing to choose between — a plain building
   * imports on click exactly as it always has.
   */
  const [importOffer, setImportOffer] = useState<{
    structures: OsmStructureOption[];
    sourceLabel: string;
    clickLng: number;
    clickLat: number;
  } | null>(null);
  /** Ids of the offered structures currently ticked. */
  const [importPicked, setImportPicked] = useState<number[]>([]);

  // Drag state lives in refs (not React state) so the move/up handlers
  // we attach via native addEventListener never read stale values.
  const headerRef     = useRef<HTMLDivElement | null>(null);
  const posRef        = useRef(pos);
  const minimizedRef  = useRef(minimized);
  const dragOffsetRef = useRef<{ dx: number; dy: number } | null>(null);
  useEffect(() => { posRef.current = pos; }, [pos]);
  useEffect(() => { minimizedRef.current = minimized; }, [minimized]);

  // Persist position + minimise state
  useEffect(() => {
    try { window.localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* quota / privacy mode */ }
  }, [pos]);
  useEffect(() => {
    try { window.localStorage.setItem(MIN_KEY, minimized ? '1' : '0'); } catch { /* ignore */ }
  }, [minimized]);

  // Re-clamp to viewport on window resize so a stored position that's
  // now off-screen (after the user resized their window) snaps back in.
  useEffect(() => {
    const onResize = () => {
      setPos(prev => {
        if (typeof window === 'undefined') return prev;
        const w = minimized ? 280 : PANEL_DEFAULT_W;
        const h = minimized ? 56  : PANEL_DEFAULT_H;
        const maxX = Math.max(8, window.innerWidth  - w - 8);
        const maxY = Math.max(8, window.innerHeight - h - 8);
        return {
          x: Math.min(Math.max(8, prev.x), maxX),
          y: Math.min(Math.max(8, prev.y), maxY),
        };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minimized]);

  // ─── Drag wiring ────────────────────────────────────────────────────
  //
  // We use NATIVE `addEventListener` (not React synthetic events) so:
  //   • events fire even if React's synthetic delegation is interfered
  //     with by another portal or stopPropagation up the tree;
  //   • move events captured on `window` keep firing when the cursor
  //     leaves the header (over the map, off the panel edge, etc.);
  //   • the very first move after mousedown is never dropped by a
  //     pending React re-render.
  //
  // Both mouse and touch are handled — touch has separate event names
  // and `clientX/Y` lives on `touches[0]`.
  useEffect(() => {
    if (!isOpen) return;
    // In centered (modal) mode the panel is fixed dead-centre — drag
    // would just confuse the operator and break the backdrop framing.
    if (openMode === 'centered') return;
    const header = headerRef.current;
    if (!header) return;

    // Bundle-version marker — if you don't see this in the browser
    // console after opening the panel, your dev server is serving a
    // stale build. Stop `npm start`, remove node_modules/.cache,
    // restart, hard-refresh.

    const isInButton = (target: EventTarget | null) =>
      !!(target as HTMLElement | null)?.closest?.('.spatial-admin-iconbtn');

    const startDrag = (clientX: number, clientY: number) => {
      const cur = posRef.current;
      dragOffsetRef.current = { dx: clientX - cur.x, dy: clientY - cur.y };
      setDragging(true);
    };

    const updateDrag = (clientX: number, clientY: number) => {
      const off = dragOffsetRef.current;
      if (!off) return;
      const w = minimizedRef.current ? 280 : PANEL_DEFAULT_W;
      const h = minimizedRef.current ? 56  : PANEL_DEFAULT_H;
      const maxX = Math.max(8, window.innerWidth  - w - 8);
      const maxY = Math.max(8, window.innerHeight - h - 8);
      const nx = Math.min(Math.max(8, clientX - off.dx), maxX);
      const ny = Math.min(Math.max(8, clientY - off.dy), maxY);
      setPos({ x: nx, y: ny });
    };

    const endDrag = () => {
      if (!dragOffsetRef.current) return;
      dragOffsetRef.current = null;
      setDragging(false);
    };

    // ── Mouse path ──
    const onMouseDown = (ev: MouseEvent) => {
      if (ev.button !== 0) return;            // primary button only
      if (isInButton(ev.target)) return;
      ev.preventDefault();
      startDrag(ev.clientX, ev.clientY);
      const onMove = (e: MouseEvent) => updateDrag(e.clientX, e.clientY);
      const onUp = () => {
        endDrag();
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup',   onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup',   onUp);
    };

    // ── Touch path ──
    const onTouchStart = (ev: TouchEvent) => {
      if (isInButton(ev.target)) return;
      const t = ev.touches[0];
      if (!t) return;
      ev.preventDefault();
      startDrag(t.clientX, t.clientY);
      const onMove = (e: TouchEvent) => {
        const m = e.touches[0]; if (!m) return;
        updateDrag(m.clientX, m.clientY);
      };
      const onUp = () => {
        endDrag();
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend',  onUp);
        window.removeEventListener('touchcancel', onUp);
      };
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend',  onUp);
      window.addEventListener('touchcancel', onUp);
    };

    header.addEventListener('mousedown',  onMouseDown);
    header.addEventListener('touchstart', onTouchStart, { passive: false });

    return () => {
      header.removeEventListener('mousedown',  onMouseDown);
      header.removeEventListener('touchstart', onTouchStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, openMode]);

  const resetPosition = useCallback(() => setPos(defaultPos()), [defaultPos]);

  // ─── Side effects ───────────────────────────────────────────────────

  const LIST_PAGE_LIMIT = 1000;

  /**
   * Load the object list.
   *
   * The panel deliberately fetches broadly rather than pushing the
   * viewport down to GeoLink on every pan. Two reasons: `moveend` fires
   * constantly, and the parent picker and the parent/child tree both
   * need rows that are off-screen — a floor's building is frequently
   * outside the current view.
   *
   * The exception is overflow. When GeoLink reports a `total` larger
   * than the page it gave us, the in-memory list is provably incomplete
   * and client-side search would lie by omission. Then, and only then,
   * we re-ask scoped to the viewport and hand the filtering to PostGIS.
   */
  const refreshRows = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const baseQuery: SpatialObjectAdminListQuery = {
        // Pull a high cap so duplicates aren't hidden behind GeoLink's
        // default page size — the operator needs to SEE every dupe to be
        // able to bulk-delete them.
        limit: LIST_PAGE_LIMIT,
        sort: serverSortFor(sortKey),
      };
      if (filterType) baseQuery.object_type = filterType;
      const ownerFilter = mineOnly && walletAddress ? walletAddress : filterOwner.trim();
      if (ownerFilter) baseQuery.owner_wallet = ownerFilter;

      let page = await geoLinkSpatialObjectsAdminClient.listObjectsPage(baseQuery);
      let scoped = false;

      const overflowed = page.total !== null && page.total > page.rows.length;
      const bounds = viewportRef.current.bounds;
      if (overflowed && bounds) {
        const term = searchTerm.trim();
        page = await geoLinkSpatialObjectsAdminClient.listObjectsPage({
          ...baseQuery,
          bbox: {
            sw: { lng: bounds.west, lat: bounds.south },
            ne: { lng: bounds.east, lat: bounds.north },
          },
          ...(term.length >= 2 ? { q: term } : {}),
          ...(filterCountry ? { country_code: filterCountry } : {}),
        });
        // Only skip our own pass when GeoLink confirms it ran one. If
        // the capability probe was unreachable the page may be
        // unfiltered, and filtering twice is harmless where trusting a
        // guess is not.
        scoped = page.serverFiltered;
      }

      setRows(page.rows);
      setTotalCount(page.total);
      setServerScoped(scoped);
      if (scoped) {
        setStatusMsg(
          `${page.total} objects match — showing the ${page.rows.length} inside the current ` +
          `map view. Pan or zoom to load a different area.`,
        );
      }
      // Re-hydrate stepped buildings on the live map after hard refresh —
      // /render alone often loses min_height bands (Atlantis entryway floats).
      for (const row of page.rows) seedLiveMapTiersFromRow(row);
    } catch (e) {
      setErrorMsg((e as Error).message);
      setRows([]);
      setTotalCount(null);
      setServerScoped(false);
    } finally {
      setLoading(false);
    }
  }, [isOpen, filterType, filterOwner, mineOnly, walletAddress, sortKey, searchTerm, filterCountry]);

  // Settle the search box before it reaches either filter path.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(listSearch), 250);
    return () => clearTimeout(t);
  }, [listSearch]);

  // Initial load + when filters change
  useEffect(() => {
    if (!isOpen) return;
    void refreshRows();
  }, [isOpen, refreshRows]);

  // Only while the list is viewport-scoped does panning need a refetch;
  // otherwise the in-memory pass already has every row.
  useEffect(() => {
    if (!isOpen || !serverScoped || !viewport.bounds) return;
    const t = setTimeout(() => { void refreshRows(); }, 400);
    return () => clearTimeout(t);
    // `refreshRows` is intentionally omitted: it re-identifies whenever a
    // filter changes, and that path already triggers its own fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, serverScoped, viewport.bounds]);

  // ─── Live viewport tracking ─────────────────────────────────────────
  // Sample the camera when the panel opens and after every pan/zoom so
  // "in view" and distance stay honest while the operator moves around.
  useEffect(() => {
    if (!isOpen || !map) return;
    const sample = () => {
      try {
        const b = map.getBounds();
        const c = map.getCenter();
        if (!b || !c) return;
        setViewport({
          bounds: { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
          center: { lng: c.lng, lat: c.lat },
        });
      } catch { /* map torn down mid-sample */ }
    };
    sample();
    map.on('moveend', sample);
    return () => { try { map.off('moveend', sample); } catch { /* ignore */ } };
  }, [isOpen, map]);

  // ─── Derived list (decorate → filter → sort) ────────────────────────
  const decoratedRows = useMemo(
    () => decorateRows(rows, {
      bounds: viewport.bounds,
      center: viewport.center,
      resolveCountry,
    }),
    [rows, viewport.bounds, viewport.center, resolveCountry],
  );

  const listFilters = useMemo<SpatialListFilters>(() => ({
    search: searchTerm,
    type: filterType,
    owner: filterOwner,
    mineOnly,
    walletAddress,
    countryIso2: filterCountry,
    inViewOnly,
    sort: sortKey,
  }), [searchTerm, filterType, filterOwner, mineOnly, walletAddress, filterCountry, inViewOnly, sortKey]);

  const visibleRows = useMemo(
    // When the page came back viewport-scoped, GeoLink already applied
    // every predicate we would re-apply here — and its id matching is a
    // prefix where ours is a substring, so a second pass would drop rows
    // the server deliberately returned.
    () => filterAndSortRows(decoratedRows, listFilters, serverScoped),
    [decoratedRows, listFilters, serverScoped],
  );

  const availableCountries = useMemo(() => countryOptions(decoratedRows), [decoratedRows]);
  const inViewCount = useMemo(
    () => decoratedRows.reduce((n, d) => (d.inView ? n + 1 : n), 0),
    [decoratedRows],
  );

  // Editor: eligible parents for the object being edited. Deliberately
  // sourced from ALL loaded rows, not the filtered list — a type filter
  // on the Objects tab must not hide the building a floor belongs to.
  const editorParentCandidates = useMemo(
    () => parentCandidates(decoratedRows, form.object_type, form.id),
    [decoratedRows, form.object_type, form.id],
  );
  const selectedParentRow = useMemo(
    () => rows.find((r) => r.id === form.parent_object_id.trim()) ?? null,
    [rows, form.parent_object_id],
  );

  // Country of the object being edited, needed to register it on chain.
  // Read from the decorated row rather than the form because FormState
  // carries no country — the list derives it from the centroid.
  const editingCountryIso2 = useMemo(() => {
    const id = (form.id ?? '').trim();
    if (!id) return null;
    return decoratedRows.find((d) => d.row.id === id)?.countryIso2 ?? null;
  }, [decoratedRows, form.id]);

  // Load rules + agents lazily when the overlay first opens
  useEffect(() => {
    if (!isOpen) return;
    void (async () => {
      try {
        const r = await geoLinkSpatialObjectsAdminClient.listExecutionRules({
          publicKey: walletAddress ?? undefined,
        });
        setRules(r);
      } catch { /* leave empty — UI shows "no rules" */ }
      if (walletAddress) {
        try {
          const a = await listAgents({ operator: walletAddress });
          setAgents(a);
        } catch { /* operator not registered yet — fine */ }
      }
    })();
  }, [isOpen, walletAddress]);

  // Cleanup draw/import handlers + live import preview when overlay closes
  useEffect(() => {
    if (isOpen) return;
    teardownDraw();
    teardownImport();
    teardownParentPick();
    clearImportPreview(map);
    // ignore deps: helper refs are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ─── Draw helpers ───────────────────────────────────────────────────

  const teardownDraw = useCallback(() => {
    if (map && drawRef.current) {
      try {
        if (drawCreateHandlerRef.current) {
          // mapbox-gl-draw emits "draw.create"/"draw.update"/"draw.delete"
          // but Mapbox's typed events don't list them — string cast is safe.
          (map as unknown as { off: (e: string, h: () => void) => void }).off('draw.create', drawCreateHandlerRef.current);
          (map as unknown as { off: (e: string, h: () => void) => void }).off('draw.update', drawCreateHandlerRef.current);
        }
        map.removeControl(drawRef.current);
      } catch { /* ignore */ }
      drawRef.current = null;
      drawCreateHandlerRef.current = null;
    }
    // Hand map clicks back to App.tsx's country handler, and restore
    // the regular panel UI.
    releaseMapClicks();
    setPickingMode(prev => (prev === 'draw' ? null : prev));
  }, [map]);

  const startDraw = useCallback((mode: 'draw_polygon' | 'draw_point' | 'draw_line_string') => {
    if (!map) return;
    teardownDraw();
    // Suppress App.tsx's country-click while we're drawing — otherwise
    // every vertex click also pops the country tooltip on top of the
    // map.
    claimMapClicks('draw');
    setPickingMode('draw');
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, point: true, line_string: true, trash: true },
      defaultMode: mode,
    });
    map.addControl(draw);
    drawRef.current = draw;
    const handler = () => {
      const features = draw.getAll().features;
      const feat = features[features.length - 1];
      if (!feat || !feat.geometry) return;
      setForm(f => ({ ...f, geometry: JSON.stringify(feat.geometry, null, 2) }));
    };
    drawCreateHandlerRef.current = handler;
    (map as unknown as { on: (e: string, h: () => void) => void }).on('draw.create', handler);
    (map as unknown as { on: (e: string, h: () => void) => void }).on('draw.update', handler);
    setStatusMsg('Drawing on map — click on the map to add vertices, double-click to finish.');
  }, [map, teardownDraw]);

  // ─── Import-from-3D-building helpers ────────────────────────────────

  const teardownImport = useCallback(() => {
    if (map && importHandlerRef.current) {
      try { map.off('click', importHandlerRef.current); } catch { /* ignore */ }
      importHandlerRef.current = null;
      map.getCanvas().style.cursor = '';
    }
    // Cancel any in-flight Overpass request so its result can't
    // arrive after teardown and clobber the form state. The fetcher
    // catches AbortError and resolves to `null`.
    if (importOsmAbortRef.current) {
      try { importOsmAbortRef.current.abort(); } catch { /* ignore */ }
      importOsmAbortRef.current = null;
    }
    // Always release the click claim — even if `map` is gone — so a
    // stuck claim can't keep the country tooltip suppressed.
    releaseMapClicks();
    setPickingMode(prev => (prev === 'import-building' ? null : prev));
  }, [map]);

  /**
   * Apply a successful OSM / Mapbox import into the form, show a live
   * 3D preview on the map, auto-fill type + name, and fly the camera.
   */
  const applyBuildingImport = useCallback((opts: {
    polygons: number[][][][];
    tiers: BuildingTier[];
    heightMeters: number;
    baseHeightMeters: number;
    sourceLabel: string;
    clickLng: number;
    clickLat: number;
    /** Absent for the Mapbox tile fallback, which measures nothing. */
    heightProvenance?: HeightProvenance;
    osmIds?: number[];
  }) => {
    const {
      polygons, tiers, heightMeters, baseHeightMeters, sourceLabel, clickLng, clickLat,
    } = opts;
    const merged =
      polygons.length === 1
        ? { type: 'Polygon' as const, coordinates: polygons[0] }
        : { type: 'MultiPolygon' as const, coordinates: polygons };
    const tiersForForm: BuildingTier[] = tiers;

    setForm((prev) => ({
      ...prev,
      // Import always produces a building footprint — promote type when
      // the operator started from a blank / placeholder template.
      object_type: prev.object_type === 'building' || !prev.id
        ? 'building'
        : prev.object_type,
      geometry: JSON.stringify(merged, null, 2),
      height_meters: String(heightMeters),
      base_height_meters: String(baseHeightMeters),
      tiers: tiersForForm,
      geometry_provenance: {
        source: sourceLabel === 'OSM Overpass'
          ? 'openstreetmap'
          : sourceLabel === 'OSM Buildings'
            ? 'osm-buildings-tiles'
            : 'mapbox-3d-tiles',
        sourceIds: opts.osmIds ?? [],
        importedAt: new Date().toISOString(),
        heightProvenance: opts.heightProvenance ?? 'unknown',
      },
    }));

    showImportPreview(map, polygons, tiersForForm, heightMeters, baseHeightMeters);
    flyToImportedPolygons(map, polygons);

    // Reverse-geocode a default name when the field is still empty.
    if (mapboxToken) {
      const token = mapboxToken;
      void (async () => {
        try {
          const url =
            `https://api.mapbox.com/geocoding/v5/mapbox.places/` +
            `${clickLng},${clickLat}.json?access_token=${token}&types=address,poi,place&limit=1`;
          const res = await fetch(url);
          if (!res.ok) return;
          const data = await res.json() as { features?: Array<{ text?: string; place_name?: string }> };
          const label = data.features?.[0]?.text || data.features?.[0]?.place_name;
          if (!label) return;
          setForm((prev) => {
            if (prev.name.trim()) return prev;
            return { ...prev, name: label };
          });
        } catch { /* non-fatal */ }
      })();
    }

    const tierSummary = tiersForForm.length > 0
      ? ` · ${tiersForForm.length} tiers (${heightMeters}m peak)`
      : '';
    setStatusMsg(
      polygons.length > 1
        ? `Imported ${polygons.length} polygons via ${sourceLabel}${tierSummary}. Review → Save.`
        : `Imported footprint + height ${heightMeters}m via ${sourceLabel}. Review → Save.`,
    );
    setErrorMsg(null);
    setTab('editor');
    teardownImport();
  }, [map, mapboxToken, teardownImport]);

  const toggleImportPicked = useCallback((id: number) => {
    setImportPicked((prev) => (prev.includes(id)
      ? prev.filter((x) => x !== id)
      : [...prev, id]));
  }, []);

  const dismissImportOffer = useCallback(() => {
    setImportOffer(null);
    setImportPicked([]);
    clearImportPreview(map);
  }, [map]);

  /**
   * Preview whatever is currently ticked, in the order the structures were
   * offered, so the map answers the question the checkboxes are asking.
   */
  useEffect(() => {
    if (!importOffer) return;
    const picked = importOffer.structures.filter((s) => importPicked.includes(s.id));
    if (picked.length === 0) {
      clearImportPreview(map);
      return;
    }
    const merged = mergeStructures(picked);
    showImportPreview(
      map,
      merged.polygons,
      merged.tiers,
      merged.heightMeters,
      merged.baseHeightMeters,
    );
    flyToImportedPolygons(map, merged.polygons);
  }, [map, importOffer, importPicked]);

  const applyImportPicked = useCallback(() => {
    if (!importOffer) return;
    const picked = importOffer.structures.filter((s) => importPicked.includes(s.id));
    if (picked.length === 0) {
      setErrorMsg('Tick at least one structure to import.');
      return;
    }
    const merged = mergeStructures(picked);
    applyBuildingImport({
      polygons: merged.polygons,
      tiers: merged.tiers,
      heightMeters: merged.heightMeters,
      baseHeightMeters: merged.baseHeightMeters,
      sourceLabel: importOffer.sourceLabel,
      heightProvenance: merged.heightProvenance,
      osmIds: merged.osmIds,
      clickLng: importOffer.clickLng,
      clickLat: importOffer.clickLat,
    });
    setImportOffer(null);
    setImportPicked([]);
  }, [importOffer, importPicked, applyBuildingImport]);

  const startImportFrom3DBuilding = useCallback(() => {
    if (!map) return;
    teardownImport();
    clearImportPreview(map);
    // Tell App.tsx to stand down — the next map click is ours.
    claimMapClicks('import-building');
    setPickingMode('import-building');
    map.getCanvas().style.cursor = 'crosshair';
    setStatusMsg('Click a building on the map — OpenStreetMap supplies every footprint polygon (including multipolygon parts) + tiered heights.');

    const handler = (e: mapboxgl.MapMouseEvent): void => { void (async () => {
      // ─── OSM-first (no Mapbox building layer required) ───────────────
      // Click lng/lat is enough. Mapbox 3D buildings are only a fallback
      // when OSM has no coverage. Requiring a Mapbox layer previously
      // blocked import on styles / zooms where buildings weren't painted.
      setStatusMsg('Querying OpenStreetMap for exact building geometry…');
      const osmCtl = new AbortController();
      importOsmAbortRef.current = osmCtl;
      let osmResult: Awaited<ReturnType<typeof fetchOsmBuildingParts>> = null;
      try {
        osmResult = await fetchOsmBuildingParts(e.lngLat.lng, e.lngLat.lat, {
          signal: osmCtl.signal,
        });
      } catch {
        /* fall through */
      }
      if (osmCtl.signal.aborted) return;
      if (importOsmAbortRef.current === osmCtl) {
        importOsmAbortRef.current = null;
      }

      if (osmResult && osmResult.tiers.length > 0) {
        const sourceLabel = osmResult.source === 'osm-overpass'
          ? 'OSM Overpass'
          : 'OSM Buildings';

        // Nothing to choose between: import the clicked building as before.
        if (osmResult.structures.length > 1) {
          setImportOffer({
            structures: osmResult.structures,
            sourceLabel,
            clickLng: e.lngLat.lng,
            clickLat: e.lngLat.lat,
          });
          setImportPicked(
            osmResult.structures.filter((s) => s.clicked).map((s) => s.id),
          );
          setTab('editor');
          teardownImport();
          return;
        }

        applyBuildingImport({
          polygons: osmResult.polygons,
          tiers: osmResult.tiers,
          heightMeters: osmResult.heightMeters,
          baseHeightMeters: osmResult.baseHeightMeters,
          sourceLabel,
          heightProvenance: osmResult.heightProvenance,
          osmIds: osmResult.diagnostics.osmIds,
          clickLng: e.lngLat.lng,
          clickLat: e.lngLat.lat,
        });
        return;
      }

      // OSM had nothing useful. Fall through to Mapbox tile heuristics
      // when a 3D building layer is present under the cursor.
      const layers = MAPBOX_BUILDING_LAYERS.filter((id) => {
        try { return !!map.getLayer(id); } catch { return false; }
      });
      if (!layers.length) {
        setStatusMsg('No OSM building at that point and no Mapbox 3D buildings in this style — zoom in or try another spot.');
        return; // keep picking mode alive
      }
      const direct = map.queryRenderedFeatures(e.point, { layers });
      const primary = direct[0];
      if (!primary) {
        setStatusMsg('No building at that point — try zooming in or clicking a different building.');
        return;
      }

      setStatusMsg('OpenStreetMap had no detailed parts here — using Mapbox tile fallback…');

      // 2) Tall, sectioned skyscrapers (Burj Khalifa, Empire State, …) get
      //    rendered as several tile-split polygon features. A point query
      //    only returns the segment under the cursor, leaving the other
      //    sections out of the imported footprint and producing the
      //    "only one column turned blue" symptom. We query a generous
      //    pixel bbox around the click and merge every building feature
      //    inside that bbox into a MultiPolygon. The previous version
      //    further filtered the merged set against the *primary* feature's
      //    geographic bbox — but for sectioned skyscrapers the clicked
      //    primary is a single small column, and its bbox + 0.5x buffer
      //    is far too tight to include the OTHER columns of the same
      //    tower (which can be 50–100m away). That filter was the actual
      //    bug behind "the multi-polygon constantly changes depending on
      //    which column I click on".
      //
      //    Two safer guards instead:
      //
      //    a) Trust the pixel bbox. RADIUS_PX=350 at typical building
      //       zooms (16–18) covers ~80–250m of ground — enough for an
      //       enormous tower, small enough to leave neighbouring blocks
      //       out at zoom 18.
      //    b) Sanity check: skip any feature whose bbox is more than
      //       MAX_GEO_DIST meters from the click point. This rejects an
      //       adjacent skyscraper that just happened to land inside the
      //       pixel bbox at lower zoom.
      const RADIUS_PX = 350;
      const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [e.point.x - RADIUS_PX, e.point.y - RADIUS_PX],
        [e.point.x + RADIUS_PX, e.point.y + RADIUS_PX],
      ];
      const nearby = map.queryRenderedFeatures(bbox, { layers });

      // Walk every coordinate in a Polygon/MultiPolygon coordinate tree.
      const visit = (coords: unknown, cb: (lng: number, lat: number) => void): void => {
        if (!Array.isArray(coords)) return;
        if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
          cb(coords[0] as number, coords[1] as number); return;
        }
        for (const c of coords) visit(c, cb);
      };

      // Compute the geographic bounding box of any Polygon/MultiPolygon.
      const bboxOf = (g: { type: string; coordinates: unknown }): { w: number; s: number; e: number; n: number } | null => {
        let w = Infinity, s = Infinity, ee = -Infinity, n = -Infinity;
        visit(g.coordinates, (lng, lat) => {
          if (lng < w)  w  = lng;
          if (lng > ee) ee = lng;
          if (lat < s)  s  = lat;
          if (lat > n)  n  = lat;
        });
        if (!Number.isFinite(w)) return null;
        return { w, s, e: ee, n };
      };

      // Convert a metres-distance threshold to per-axis degree
      // tolerances at the click latitude. Used everywhere we compare
      // bbox separations to a metres value.
      const click = e.lngLat;
      const M_PER_DEG_LAT = 111_320;
      const cosLat = Math.cos((click.lat * Math.PI) / 180);
      const ADJ_M = 5;            // two polygons are "touching" if their bboxes are within 5m
      const HARD_CAP_M = 250;     // never grow more than 250m from click (Burj's footprint ~150m)
      const adjLatDeg  = ADJ_M / M_PER_DEG_LAT;
      const adjLngDeg  = ADJ_M / (M_PER_DEG_LAT * cosLat);
      const capLatDeg  = HARD_CAP_M / M_PER_DEG_LAT;
      const capLngDeg  = HARD_CAP_M / (M_PER_DEG_LAT * cosLat);

      // Two bboxes are adjacent if they overlap or sit within ADJ_M of
      // each other. Sectioned skyscrapers (Burj Khalifa, Empire State,
      // …) get rendered as several touching polygon features — this
      // catches all of them. Unrelated towers a clear block away
      // (>5m gap to any merged polygon) stay excluded.
      const adjacent = (
        a: { w: number; s: number; e: number; n: number },
        b: { w: number; s: number; e: number; n: number },
      ): boolean => {
        const dxDeg = Math.max(0, b.w - a.e, a.w - b.e);
        const dyDeg = Math.max(0, b.s - a.n, a.s - b.n);
        return dxDeg <= adjLngDeg && dyDeg <= adjLatDeg;
      };

      // Hard cap — never accept a feature whose bbox is more than
      // HARD_CAP_M from the click point. Acts as a sanity guard so a
      // chain of adjacent buildings can't propagate the merge across a
      // whole city block.
      const withinHardCap = (b: { w: number; s: number; e: number; n: number }): boolean => {
        const dxDeg = Math.max(0, b.w - click.lng, click.lng - b.e);
        const dyDeg = Math.max(0, b.s - click.lat, click.lat - b.n);
        return dxDeg <= capLngDeg && dyDeg <= capLatDeg;
      };

      // Sanity cap on a single polygon's own dimensions. Mapbox /
      // OSM tile data sometimes lumps a building's surroundings
      // (the property boundary, the plaza concrete, etc.) into one
      // giant rectangle classified as a "building" with a non-
      // trivial height. For Burj Khalifa specifically there's a
      // 900m × 330m polygon at h ≈ 505m that, once accepted into
      // the merge, extrudes from the ground to halfway up the
      // tower as a single fat 1 km block — exactly the "lower half
      // is just the base" symptom.
      //
      // Real building footprints are bounded; even Burj Khalifa is
      // only ~150 m at its widest. Cap each polygon's longest side
      // at MAX_BLDG_DIM_M so absurdly-large outline tiles get
      // dropped from the merge. The primary clicked feature is
      // exempt — if the operator actually clicked a giant feature,
      // we trust that intent over the size cap.
      const MAX_BLDG_DIM_M = 300;
      const maxBldgLatDeg  = MAX_BLDG_DIM_M / M_PER_DEG_LAT;
      const maxBldgLngDeg  = MAX_BLDG_DIM_M / (M_PER_DEG_LAT * cosLat);
      const reasonableSize = (b: { w: number; s: number; e: number; n: number }): boolean => (
        (b.e - b.w) <= maxBldgLngDeg && (b.n - b.s) <= maxBldgLatDeg
      );

      // Helper: pull a feature's display height from its OSM/Mapbox
      // properties. Mapbox/MapTiler populate `render_height` with the
      // value the visual extrusion uses (matches what the user sees on
      // the map). OSM's semantic `height` is the fallback. Returns NaN
      // when neither is present so callers can decide what to do.
      const featureHeight = (f: typeof primary): number => {
        const p = (f.properties || {}) as {
          height?: number;
          render_height?: number;
        };
        const h = Number(p.render_height ?? p.height);
        return Number.isFinite(h) ? h : NaN;
      };
      const featureMinHeight = (f: typeof primary): number => {
        const p = (f.properties || {}) as {
          min_height?: number;
          render_min_height?: number;
        };
        const v = Number(p.render_min_height ?? p.min_height);
        return Number.isFinite(v) ? v : 0;
      };

      // Build a working set of unique candidate features (deduped by
      // geometry signature) with their pre-computed bboxes + per-tier
      // heights so the merge can preserve setbacks (Burj Khalifa, …).
      type Candidate = {
        f: typeof primary;
        g: { type: string; coordinates: unknown };
        bbox: { w: number; s: number; e: number; n: number };
        sig: string;
        h: number;          // feature height (NaN if unknown)
        bh: number;         // feature base / min_height (0 if unknown)
      };
      const seen = new Set<string>();
      const candidates: Candidate[] = [];
      for (const f of [primary, ...nearby]) {
        const g = f.geometry as { type: string; coordinates: unknown };
        if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') continue;
        const sig = JSON.stringify(g.coordinates);
        if (seen.has(sig)) continue;
        seen.add(sig);
        const b = bboxOf(g);
        if (!b) continue;
        candidates.push({
          f, g, bbox: b, sig,
          h:  featureHeight(f),
          bh: featureMinHeight(f),
        });
      }

      const primaryHeight = featureHeight(primary);

      // Two bboxes "belong to the same building" when they OVERLAP in
      // 2D plan, OR are within ±2m of each other (shared-wall
      // construction). Stepped towers like Burj Khalifa publish each
      // tier as a separate polygon with a smaller footprint that sits
      // INSIDE the lower tier's footprint — bbox overlap catches that
      // and lets the import collect every tier in one click. Distinct
      // adjacent buildings (Dubai Mall vs Burj Khalifa) sit side-by-
      // side in 2D, so their bboxes don't overlap; we keep a tiny ±2m
      // adjacency band only for buildings that share an actual wall.
      const SHARED_WALL_M = 2;
      const wallLatDeg = SHARED_WALL_M / M_PER_DEG_LAT;
      const wallLngDeg = SHARED_WALL_M / (M_PER_DEG_LAT * cosLat);
      const sameBuilding = (
        a: { w: number; s: number; e: number; n: number },
        b: { w: number; s: number; e: number; n: number },
      ): boolean => {
        const dxDeg = Math.max(0, b.w - a.e, a.w - b.e);
        const dyDeg = Math.max(0, b.s - a.n, a.s - b.n);
        return dxDeg <= wallLngDeg && dyDeg <= wallLatDeg;
      };
      // Defensive: if `adjacent` (the broader ±5m test we built earlier
      // for shared-podium constructions) ever needs to come back, it
      // still exists above. The new growing-union below uses the
      // strict overlap-or-shared-wall test.
      void adjacent;

      // Growing union: start from the primary candidate, then
      // iteratively pull in every remaining candidate whose bbox
      // OVERLAPS (or shares a wall with) ANY already-accepted bbox
      // AND is inside the hard distance cap. Loop until no more
      // additions — that gives us the entire stepped tower structure
      // INCLUDING tiers at different heights, but never bridges into
      // a separate adjacent building.
      const primarySig = JSON.stringify((primary.geometry as { coordinates: unknown }).coordinates);
      const acceptedIdx = new Set<number>();
      candidates.forEach((c, i) => { if (c.sig === primarySig) acceptedIdx.add(i); });
      if (acceptedIdx.size === 0 && candidates.length > 0) acceptedIdx.add(0);
      const oversizedRejected: Array<{ i: number; widthM: number; heightM: number; h: number }> = [];
      let grew = true;
      while (grew) {
        grew = false;
        const acceptedArr = Array.from(acceptedIdx);
        for (let i = 0; i < candidates.length; i++) {
          if (acceptedIdx.has(i)) continue;
          const c = candidates[i];
          if (!withinHardCap(c.bbox)) continue;
          // Reject oversize polygons (property-boundary / plaza
          // outlines mistakenly classified as buildings). Logged
          // so a future "missed a tier" report can verify whether
          // a legit setback got dropped here.
          if (!reasonableSize(c.bbox)) {
            if (!oversizedRejected.some(r => r.i === i)) {
              oversizedRejected.push({
                i,
                widthM:  Math.round((c.bbox.e - c.bbox.w) * M_PER_DEG_LAT * cosLat),
                heightM: Math.round((c.bbox.n - c.bbox.s) * M_PER_DEG_LAT),
                h:       c.h,
              });
            }
            continue;
          }
          let touches = false;
          for (let k = 0; k < acceptedArr.length; k++) {
            if (sameBuilding(c.bbox, candidates[acceptedArr[k]].bbox)) { touches = true; break; }
          }
          if (touches) { acceptedIdx.add(i); grew = true; }
        }
      }

      // Materialise the merged polygon list AND a per-polygon tier
      // table. Each entry in `tiers` records the (min_height, height)
      // range that the renderer should extrude THAT specific polygon
      // between — Burj Khalifa's wide ground footprint goes 0→podium,
      // its mid setback goes podium→mid_top, the spire goes
      // mid_top→828, and so on. Without per-polygon tiers the
      // building looks like one fat block extruded the full height.
      const polygons: number[][][][] = [];
      // Per-polygon tier info, captured as we flatten polygons. We
      // build it during the same loop so `polygon_index` always lines
      // up with `polygons[i]`.
      const tiers: BuildingTier[] = [];
      // Per-polygon bbox + area cache. Used by the envelope-clip pass
      // below so we don't recompute bbox/area for every (i, j) pair.
      const tierBboxes: Array<{ w: number; s: number; e: number; n: number }> = [];
      const tierAreas:  number[] = [];
      let maxOverallHeight = -Infinity;
      let minOverallBase   = Infinity;
      const acceptedArr = Array.from(acceptedIdx);
      for (let ai = 0; ai < acceptedArr.length; ai++) {
        const c = candidates[acceptedArr[ai]];
        const tierH  = Number.isFinite(c.h)  ? c.h  : (Number.isFinite(primaryHeight) ? primaryHeight : 30);
        const tierBh = Number.isFinite(c.bh) ? c.bh : 0;
        const pushPolygon = (poly: number[][][]) => {
          const idx = polygons.length;
          polygons.push(poly);
          tiers.push({ polygon_index: idx, min_height: tierBh, height: tierH });
          const pb = bboxOf({ type: 'Polygon', coordinates: poly });
          tierBboxes.push(pb ?? { w: 0, s: 0, e: 0, n: 0 });
          // Approx 2D plan area in square degrees; only used for
          // size-comparison ratios (envelope detection), not for any
          // absolute distance calc, so degree units are fine.
          tierAreas.push(pb ? Math.max(0, (pb.e - pb.w) * (pb.n - pb.s)) : 0);
        };
        if (c.g.type === 'Polygon') {
          pushPolygon(c.g.coordinates as number[][][]);
        } else {
          for (const p of c.g.coordinates as number[][][][]) pushPolygon(p);
        }
        if (tierH > maxOverallHeight) maxOverallHeight = tierH;
        if (tierBh < minOverallBase)  minOverallBase   = tierBh;
      }
      if (!Number.isFinite(maxOverallHeight)) {
        maxOverallHeight = Number.isFinite(primaryHeight) ? primaryHeight : 30;
      }
      if (!Number.isFinite(minOverallBase)) minOverallBase = 0;

      const isContained = (
        inner: { w: number; s: number; e: number; n: number },
        outer: { w: number; s: number; e: number; n: number },
      ): boolean => (
        inner.w >= outer.w && inner.e <= outer.e &&
        inner.s >= outer.s && inner.n <= outer.n
      );
      const ENVELOPE_AREA_RATIO = 0.7; // contained must be ≤70% of envelope area

      // min_height reconstruction. Mapbox's vector tiles for
      // stepped towers (Burj Khalifa is the canonical case)
      // routinely emit EVERY polygon with `min_height = 0` — even
      // the upper-tier setbacks that should sit on top of the
      // podium. Without this pass each polygon extrudes from the
      // ground; the widest one (typically the building's outer
      // outline) wins the visual in its full height range and the
      // stepped silhouette collapses into one fat block from
      // ground to wherever-that-polygon-ends.
      //
      // Detect the all-zeros case and walk the containment graph:
      // for every polygon `inner`, lift its base to the height of
      // the TALLEST larger-and-shorter polygon `outer`. That makes
      // smaller polygons stack on top of their containers exactly
      // like real building setbacks (podium → mid → spire).
      //
      // Stacking test: the inner polygon's bbox CENTER must fall
      // inside the outer's bbox AND outer.area > inner.area. This
      // is intentionally LOOSER than strict bbox containment —
      // Mapbox's per-tier polygons share roughly the same center
      // but exhibit tier-to-tier bbox jitter (one tier may extend
      // a few metres past another in a single axis). A strict
      // containment test would reject those legitimate stacks; the
      // center-inside test only rejects polygons that have clearly
      // drifted off-axis.
      const centerInside = (
        innerB: { w: number; s: number; e: number; n: number },
        outerB: { w: number; s: number; e: number; n: number },
      ): boolean => {
        const cx = (innerB.w + innerB.e) / 2;
        const cy = (innerB.s + innerB.n) / 2;
        return cx >= outerB.w && cx <= outerB.e && cy >= outerB.s && cy <= outerB.n;
      };
      const allBasesAtZero = tiers.length >= 2 && tiers.every(t => t.min_height === 0);
      const reconstructedBases: Array<{ i: number; from: number; to: number }> = [];
      if (allBasesAtZero) {
        for (let i = 0; i < tiers.length; i++) {
          const inner     = tiers[i];
          const innerBbox = tierBboxes[i];
          const innerArea = tierAreas[i];
          if (!innerArea) continue;
          let bestBase = 0;
          for (let j = 0; j < tiers.length; j++) {
            if (i === j) continue;
            const outer     = tiers[j];
            const outerBbox = tierBboxes[j];
            const outerArea = tierAreas[j];
            if (!outerArea) continue;
            // outer must be larger (the lower-tier wider one)
            if (outerArea <= innerArea) continue;
            // outer must be strictly shorter so inner stacks on top
            if (outer.height >= inner.height) continue;
            // inner's center must fall within outer's bbox
            if (!centerInside(innerBbox, outerBbox)) continue;
            if (outer.height > bestBase) bestBase = outer.height;
          }
          if (bestBase > 0) {
            reconstructedBases.push({ i, from: inner.min_height, to: bestBase });
            inner.min_height = bestBase;
          }
        }
        // Update minOverallBase to reflect any non-zero floors after
        // reconstruction. (We deliberately leave maxOverallHeight
        // alone — the form's "Height (m)" should still capture the
        // tallest tier's top.) For Burj Khalifa the lowest base will
        // still be 0 (the wide podium polygon), so the form-level
        // base typically stays at 0.
        let newMinBase = Infinity;
        for (const t of tiers) if (t.min_height < newMinBase) newMinBase = t.min_height;
        if (Number.isFinite(newMinBase)) minOverallBase = newMinBase;
      }
      // Height-only fallback. The spatial test catches concentric
      // setbacks where a single shorter polygon contains the
      // taller one. But Burj Khalifa's tile data has WIDE OUTLINE
      // polygons at each tier (e.g. one Y-shape outline at h=70,
      // another at h=105, …) interleaved with SUB-PIECES at lower
      // heights (three small Y-wing columns at h=35). No
      // individual h=35 column is bigger than the h=70 outline,
      // so the spatial test can't pick any of them as a base —
      // result: every wide outline polygon stays rooted at the
      // foundation tier (15m) and renders as a tall slab from
      // 15m all the way up to its top.
      //
      // Pragmatic workaround: lift only polygons whose CURRENT
      // slab thickness exceeds `FAT_ENVELOPE_THICKNESS_M = 200m`.
      // Those are the "fat envelope" cases (e.g. Burj Khalifa
      // poly 16 at 0→505m, poly 9 at 15→460m) — wide outline
      // polygons whose height range overshoots into many setbacks
      // and renders as one big block. Polygons whose existing
      // slab is already thin (< 200m) are LEGITIMATE tier
      // outlines that should stay anchored to the podium / their
      // spatial-reconstructed base; lifting those creates "the
      // base looks floating" or "the top is floating" effects
      // because nothing wide fills the vacated range below them.
      // The growing-union step already filtered the polygon set
      // to a single building so it's safe for non-fat polygons
      // to overlap each other — fill-extrusion just renders the
      // widest at each height, giving a naturally stepped tower
      // silhouette below the threshold.
      const FAT_ENVELOPE_THICKNESS_M = 200;
      const sortedUniqueHeights = Array.from(new Set(tiers.map(t => t.height))).sort((a, b) => a - b);
      const nextShorter = new Map<number, number>();
      for (let k = 1; k < sortedUniqueHeights.length; k++) {
        nextShorter.set(sortedUniqueHeights[k], sortedUniqueHeights[k - 1]);
      }
      const heightFallbackLifts: Array<{ i: number; from: number; to: number; height: number; thicknessBefore: number }> = [];
      if (allBasesAtZero) {
        for (let i = 0; i < tiers.length; i++) {
          const t = tiers[i];
          const currentThickness = t.height - t.min_height;
          if (currentThickness < FAT_ENVELOPE_THICKNESS_M) continue;
          const expected = nextShorter.get(t.height);
          if (expected === undefined) continue;
          if (expected <= t.min_height) continue;
          // Skip lifts that would make the resulting slab too thin
          // to render meaningfully.
          if (t.height - expected < 5) continue;
          heightFallbackLifts.push({
            i,
            from:            t.min_height,
            to:              expected,
            height:          t.height,
            thicknessBefore: currentThickness,
          });
          t.min_height = expected;
        }
        // Refresh form-level base after the second pass.
        let newMinBase2 = Infinity;
        for (const t of tiers) if (t.min_height < newMinBase2) newMinBase2 = t.min_height;
        if (Number.isFinite(newMinBase2)) minOverallBase = newMinBase2;
      }

      // Envelope clipping. Safety net for tiles that DID supply
      // some min_heights but also published a wide outer-outline
      // polygon whose `height` overshoots into a contained
      // polygon's base — e.g. a "skirt" polygon at (0 → 400m) that
      // covers smaller setback polygons stacked above it. For each
      // polygon `i`, find every contained polygon `j` whose
      // `min_height` falls between `i`'s base and current top, and
      // clip `i` down to `j.min_height` so they stack instead of
      // overlap. After the reconstruction pass above this is
      // usually a no-op for stepped towers, but it still helps for
      // partially-populated tile data.
      const envelopeClips: Array<{ i: number; from: number; to: number; cause: number }> = [];
      for (let i = 0; i < tiers.length; i++) {
        const a = tiers[i];
        const aBbox = tierBboxes[i];
        const aArea = tierAreas[i];
        if (!aArea) continue;
        let newTop = a.height;
        for (let j = 0; j < tiers.length; j++) {
          if (i === j) continue;
          const b = tiers[j];
          const bBbox = tierBboxes[j];
          const bArea = tierAreas[j];
          if (!bArea) continue;
          if (bArea > aArea * ENVELOPE_AREA_RATIO) continue;
          if (!isContained(bBbox, aBbox)) continue;
          // `b` is meaningfully smaller AND geographically inside `a`.
          // If `b` starts below `a`'s top, clip `a` down to `b`'s
          // base so the two polygons stack instead of overlap.
          if (b.min_height >= a.min_height && b.min_height < newTop) {
            newTop = b.min_height;
          }
        }
        if (newTop !== a.height && newTop > a.min_height) {
          envelopeClips.push({ i, from: a.height, to: newTop, cause: -1 });
          a.height = newTop;
        }
      }

      // Detect whether the captured polygons actually have distinct
      // tier heights (>5m apart). When every polygon shares the same
      // height (a flat building, or Mapbox tile data that didn't
      // expose tiers), we strip `tiers` and fall back to the simpler
      // `height_meters` / `base_height_meters` round-trip — keeps
      // single-tier records clean.
      const TIER_DISTINCT_M = 5;
      const distinctTopHeights = new Set<number>();
      for (const t of tiers) {
        // Bucket heights to the nearest TIER_DISTINCT_M so trivial
        // float-precision differences (Mapbox tiles often disagree by
        // ~1cm) don't get treated as separate tiers.
        distinctTopHeights.add(Math.round(t.height / TIER_DISTINCT_M) * TIER_DISTINCT_M);
      }
      const isMultiTier = distinctTopHeights.size >= 2;
      const tiersForForm: BuildingTier[] = isMultiTier ? tiers : [];

      // Compatibility shims so the legacy single-tier codepath keeps
      // working. `maxHeight` and `minBase` feed the form fields just
      // like before; tiers (when present) are layered on top via
      // `metadata.tiers` and the renderer's tier-expansion path.
      const maxHeight = maxOverallHeight;
      const minBase   = minOverallBase;
      const merged =
        polygons.length === 0
          ? primary.geometry // shouldn't happen — we already checked primary
          : polygons.length === 1
            ? { type: 'Polygon',      coordinates: polygons[0] }
            : { type: 'MultiPolygon', coordinates: polygons };

      const heightMeters     = maxHeight;
      const baseHeightMeters = minBase;
      // Flatten merged geometry back into a polygon list for the shared
      // apply helper (preview + auto-name + fly-to).
      let polysForApply: number[][][][] = polygons;
      if (polysForApply.length === 0 && merged && 'coordinates' in merged) {
        const g = merged as { type: string; coordinates: unknown };
        polysForApply = g.type === 'Polygon'
          ? [g.coordinates as number[][][]]
          : (g.coordinates as number[][][][]);
      }
      if (polysForApply.length === 0) {
        setStatusMsg('Could not extract a building footprint — try another click.');
        return;
      }
      applyBuildingImport({
        polygons: polysForApply,
        tiers: tiersForForm,
        heightMeters: Number.isFinite(heightMeters) ? heightMeters : 30,
        baseHeightMeters: Number.isFinite(baseHeightMeters) ? baseHeightMeters : 0,
        sourceLabel: isMultiTier
          ? `Mapbox tiles (${distinctTopHeights.size} tier heights)`
          : 'Mapbox tiles',
        clickLng: e.lngLat.lng,
        clickLat: e.lngLat.lat,
      });
    })(); };
    importHandlerRef.current = handler;
    map.on('click', handler);
  }, [map, teardownImport, applyBuildingImport]);

  // ─── Address search + use my location ───────────────────────────────

  const [search, setSearch] = useState('');

  const flyToAddress = useCallback(async () => {
    if (!map || !search.trim() || !mapboxToken) return;
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(search.trim())}.json?access_token=${mapboxToken}&limit=1`;
      const res = await fetch(url);
      const data = await res.json();
      const f = data?.features?.[0];
      if (!f) { setStatusMsg('No matching location.'); return; }
      const [lng, lat] = f.center as [number, number];
      map.flyTo({ center: [lng, lat], zoom: 17, pitch: 50 });
      setStatusMsg(`Flew to ${f.place_name}.`);
    } catch (e) {
      setErrorMsg(`Geocoding failed: ${(e as Error).message}`);
    }
  }, [map, mapboxToken, search]);

  const usePlayerLocation = useCallback(() => {
    const lng = playerLocation?.longitude;
    const lat = playerLocation?.latitude;
    if (lng == null || lat == null) {
      setErrorMsg('No GeoTrust player location available — enable telemetry first.');
      return;
    }
    setForm(f => ({
      ...f,
      geometry: JSON.stringify({ type: 'Point', coordinates: [lng, lat] }, null, 2),
    }));
    map?.flyTo({ center: [lng, lat], zoom: 17 });
    setStatusMsg('Set Point geometry to your current GeoTrust position.');
  }, [map, playerLocation]);

  /**
   * Snap the form's geometry to the live map's current centre. Provides
   * a quick fix-up path for objects that ended up in the wrong place
   * (typically because the country-tooltip quick-add originally used the
   * country centroid as a placeholder). Operators just fly the map to the
   * real location and tap this — instant relocation.
   */
  const useMapCenter = useCallback(() => {
    if (!map) {
      setErrorMsg('Map is not ready yet.');
      return;
    }
    const c = map.getCenter();
    setForm(f => ({
      ...f,
      geometry: JSON.stringify({ type: 'Point', coordinates: [c.lng, c.lat] }, null, 2),
    }));
    setStatusMsg('Set Point geometry to the current map centre. Refine with Polygon / Import 3D building when ready.');
  }, [map]);

  /**
   * Import geometry from a vector file (GeoJSON / KML / GPX / zipped
   * Shapefile). Loads the first feature's geometry into the form's
   * geometry field and flies the map to it. Multi-feature files use the
   * first feature — spatial objects carry a single geometry.
   */
  const importVector = useCallback(async (file: File) => {
    setErrorMsg(null);
    setStatusMsg(`Parsing ${file.name}…`);
    try {
      const { featureCollection, format, count } = await parseVectorFile(file);
      const first = featureCollection.features[0];
      if (!first?.geometry) {
        setErrorMsg('No geometry found in the imported file.');
        return;
      }
      setForm(f => ({ ...f, geometry: JSON.stringify(first.geometry, null, 2) }));

      // Fly to the imported geometry's first coordinate when we can find one.
      try {
        const flat: number[] = [];
        const walk = (c: unknown): void => {
          if (Array.isArray(c)) {
            if (typeof c[0] === 'number' && typeof c[1] === 'number') {
              flat.push(c[0], c[1]);
            } else c.forEach(walk);
          }
        };
        walk((first.geometry as { coordinates?: unknown }).coordinates);
        if (flat.length >= 2 && map) {
          map.flyTo({ center: [flat[0], flat[1]], zoom: 14 });
        }
      } catch { /* non-fatal */ }

      const more = count > 1 ? ` (used the first of ${count} features)` : '';
      setStatusMsg(`Imported ${format} geometry from ${file.name}${more}.`);
    } catch (e) {
      setErrorMsg(`Import failed: ${(e as Error).message}`);
    }
  }, [map]);

  // ─── Form helpers ───────────────────────────────────────────────────

  const applyTemplate = useCallback((tplId: string) => {
    const tpl = QUICK_TEMPLATES.find(t => t.id === tplId);
    if (!tpl) return;
    setForm(prev => {
      const next = { ...prev };
      // Apply non-destructively — only set fields that are still default/empty.
      const apply = <K extends keyof FormState>(k: K, v: FormState[K]) => {
        const cur = next[k];
        if (cur === '' || cur === EMPTY_FORM[k]) next[k] = v;
      };
      if (tpl.defaults.object_type)        apply('object_type', tpl.defaults.object_type);
      if (tpl.defaults.visibility)         apply('visibility',  tpl.defaults.visibility);
      if (tpl.defaults.height_meters != null)
        apply('height_meters', String(tpl.defaults.height_meters));
      if (tpl.defaults.base_height_meters != null)
        apply('base_height_meters', String(tpl.defaults.base_height_meters));
      if (tpl.defaults.floor_number != null)
        apply('floor_number', String(tpl.defaults.floor_number));
      return next;
    });
    setStatusMsg(`Template applied: ${tpl.label}.`);
  }, []);

  const onRuleChange = useCallback((idStr: string) => {
    setForm(prev => {
      const rule = rules.find(r => String(r.id) === idStr);
      const contractAddr = rule?.contract_address ?? rule?.contractAddress ?? '';
      const fnName       = rule?.function_name    ?? rule?.functionName    ?? '';
      return {
        ...prev,
        linked_execution_rule_id: idStr,
        linked_contract_address:  prev.linked_contract_address || (contractAddr || ''),
        linked_contract_function: prev.linked_contract_function || (fnName || ''),
      };
    });
  }, [rules]);

  const startNew = useCallback((tplId?: string) => {
    setEditing(false);
    setForm(EMPTY_FORM);
    setStatusMsg(null);
    setErrorMsg(null);
    if (tplId) applyTemplate(tplId);
    setTab('editor');
  }, [applyTemplate]);

  const startEdit = useCallback((row: SpatialObjectAdminRow) => {
    setEditing(true);
    setForm(formFromRow(row));
    seedLiveMapTiersFromRow(row);
    setStatusMsg(null);
    setErrorMsg(null);
    setTab('editor');
  }, []);

  /** Typical storey height used to stack auto-generated floors. */
  const DEFAULT_FLOOR_HEIGHT_M = 4;

  /**
   * Start a new object nested under `parent`. Everything the child
   * almost certainly shares with its parent is pre-filled — owner,
   * visibility, colour, and the parent footprint as geometry — so the
   * operator shrinks and adjusts rather than redrawing from scratch.
   */
  const startChild = useCallback((parent: SpatialObjectAdminRow) => {
    const childType = defaultChildType(parent.objectType);
    const parentMeta = (parent.metadata ?? {}) as Record<string, unknown>;
    const parentBase = Number(parent.baseHeightMeters);
    const base = Number.isFinite(parentBase) ? parentBase : 0;

    let floorNumber = '';
    let baseHeight = String(base);
    let height = String(Number.isFinite(Number(parent.heightMeters)) ? Number(parent.heightMeters) : 30);
    if (childType === 'floor' || childType === 'slice') {
      const n = nextFloorNumber(rowsRef.current, parent.id);
      floorNumber = String(n);
      const floorBase = base + (n - 1) * DEFAULT_FLOOR_HEIGHT_M;
      baseHeight = String(floorBase);
      height = String(floorBase + DEFAULT_FLOOR_HEIGHT_M);
    }

    const parentLabel = parent.name || parent.id.slice(0, 8);
    const childLabel = objectTypeLabel(childType);

    setEditing(false);
    setForm({
      ...EMPTY_FORM,
      object_type: childType,
      name: floorNumber ? `${parentLabel} — ${childLabel} ${floorNumber}` : `${parentLabel} — ${childLabel}`,
      owner_wallet: parent.ownerWallet ?? '',
      visibility: parent.visibility ?? 'public',
      color_key: typeof parentMeta.color_key === 'string' ? parentMeta.color_key : EMPTY_FORM.color_key,
      // Seed from the parent footprint so the operator can shrink it.
      geometry: parent.geometry
        ? JSON.stringify(coercePolygonsToMultiPolygon(parent.geometry), null, 2)
        : '',
      base_height_meters: baseHeight,
      height_meters: height,
      floor_number: floorNumber,
      parent_object_id: parent.id,
      // A child belongs to whoever owns the parent unless the operator
      // says otherwise (ADR-06: inheritance is explicit at write time,
      // never implied at read time).
      organization_id: typeof parentMeta.organization_id === 'string'
        ? parentMeta.organization_id.trim().toLowerCase()
        : '',
    });
    setErrorMsg(null);
    setStatusMsg(
      `New ${childLabel.toLowerCase()} under "${parentLabel}". Geometry is a copy of the ` +
      `parent footprint — redraw or shrink it before saving.`,
    );
    setTab('editor');
  }, []);

  /** Fly the live map to a listed object. */
  const zoomToRow = useCallback((d: DecoratedRow) => {
    if (!map || !d.point) return;
    try {
      map.flyTo({ center: [d.point.lng, d.point.lat], zoom: Math.max(map.getZoom(), 16) });
    } catch { /* map torn down */ }
  }, [map]);

  // ─── Pick-parent-on-map ─────────────────────────────────────────────

  const teardownParentPick = useCallback(() => {
    if (map && parentPickHandlerRef.current) {
      try { map.off('click', parentPickHandlerRef.current); } catch { /* ignore */ }
      parentPickHandlerRef.current = null;
      try { map.getCanvas().style.cursor = ''; } catch { /* ignore */ }
    }
    releaseMapClicks();
    setPickingMode(prev => (prev === 'parent' ? null : prev));
  }, [map]);

  const startPickParent = useCallback(() => {
    if (!map) return;
    teardownParentPick();
    claimMapClicks('pick-parent');
    setPickingMode('parent');
    try { map.getCanvas().style.cursor = 'crosshair'; } catch { /* ignore */ }
    setStatusMsg('Click the object on the map that should be the parent.');

    const handler = (e: mapboxgl.MapMouseEvent): void => {
      const current = formRef.current;
      // Decorate fresh from the ref — the handler outlives any render
      // that produced `decoratedRows`, and picking needs no distances.
      const candidates = parentCandidates(
        decorateRows(rowsRef.current, { bounds: null, center: null }),
        current.object_type,
        current.id,
      );
      const hit = pickRowAt(candidates, { lng: e.lngLat.lng, lat: e.lngLat.lat });
      teardownParentPick();
      if (!hit) {
        setErrorMsg(
          `No eligible parent found at that point. Eligible types for a ` +
          `${objectTypeLabel(current.object_type).toLowerCase()}: ` +
          `${(PARENT_TYPES_FOR[current.object_type] ?? []).map(objectTypeLabel).join(', ') || 'any'}.`,
        );
        return;
      }
      setErrorMsg(null);
      setForm(prev => ({ ...prev, parent_object_id: hit.row.id }));
      setStatusMsg(`Parent set to "${hit.row.name || hit.row.id.slice(0, 8)}".`);
    };

    parentPickHandlerRef.current = handler;
    map.on('click', handler);
  }, [map, teardownParentPick]);

  // ─── External openParams handoff (from country tooltip) ─────────────
  //
  // The country tooltip's spatial section can ask the floating panel to
  // do one of:
  //   • edit a specific object (`objectId`),
  //   • start a new object from a template, optionally placed at a
  //     `center` lng/lat (used for the "+ Building / + Zone / + Event
  //     here" buttons),
  //   • or just open the list (no params, or only a bbox we treat as
  //     informational — there's no bbox filter UI today).
  //
  // After applying, we notify the parent via `onParamsConsumed` so it
  // can clear the request and avoid re-applying it on subsequent
  // re-renders.
  useEffect(() => {
    if (!isOpen || !openParams) return;
    let cancelled = false;
    (async () => {
      try {
        if (openParams.objectId) {
          // Resolve the row from the list — that's the only admin
          // endpoint reliably routed through the GeoLink proxy in
          // every build. We deliberately do NOT call
          // GET /api/admin/spatial-objects/:id; on the deployed
          // backend it returns 404 and just produces console noise
          // without adding any data the list doesn't already have.
          //
          // Order: in-memory cache (rowsRef) → fresh listObjects()
          // call → friendly inline error.
          let row: SpatialObjectAdminRow | null =
            rowsRef.current.find((r) => r.id === openParams.objectId) ?? null;
          if (!row) {
            try {
              const list = await geoLinkSpatialObjectsAdminClient.listObjects({ limit: 1000 });
              if (!cancelled) {
                rowsRef.current = list;
                setRows(list);
              }
              row = list.find((r) => r.id === openParams.objectId) ?? null;
            } catch (e) {
            }
          }
          if (!cancelled && row) {
            setEditing(true);
            setForm(formFromRow(row));
            seedLiveMapTiersFromRow(row);
            setTab('editor');
          } else if (!cancelled) {
            setErrorMsg(
              `Spatial object ${openParams.objectId} not in the listed ` +
              `set for this wallet. Open the Objects tab and pick it ` +
              `from the list, or clear the "Mine only" filter if the ` +
              `object belongs to a different owner.`,
            );
            setTab('objects');
          }
        } else if (openParams.template) {
          if (cancelled) return;
          setEditing(false);
          setForm(EMPTY_FORM);
          applyTemplate(openParams.template);
          if (openParams.center) {
            const c = openParams.center;
            setForm((prev) => ({
              ...prev,
              geometry: JSON.stringify({ type: 'Point', coordinates: [c.lng, c.lat] }, null, 2),
            }));
            // Only nudge the camera when explicitly asked. If the
            // operator was already zoomed in (e.g., looking at the
            // Burj Khalifa) we'd rather keep their view than yank it
            // back to the country centroid.
            if (openParams.flyToCenter) {
              try { map?.flyTo({ center: [c.lng, c.lat], zoom: 16 }); } catch { /* ignore */ }
            }
          }
          setTab('editor');
        } else {
          // No object / template — just open the list. A bbox means the
          // operator came in from a country tooltip, so scope the list to
          // what is on screen; they can untick "In view only" to see all.
          if (cancelled) return;
          setTab('objects');
          if (openParams.bbox) {
            setInViewOnly(true);
            setStatusMsg('Scoped to objects in the current map view — untick "In view only" to see everything.');
          }
        }
      } finally {
        if (!cancelled) onParamsConsumed?.();
      }
    })();
    return () => { cancelled = true; };
    // We deliberately ignore `applyTemplate`/`map` here — re-running on
    // those would cause stale params to re-apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, openParams]);

  // ─── Persist ────────────────────────────────────────────────────────

  const saveForm = useCallback(async () => {
    setErrorMsg(null);
    setStatusMsg(null);
    if (!walletAddress) {
      setErrorMsg('Connect a wallet before creating or editing spatial objects.');
      return;
    }

    // Editing an existing object is authorized here, not only on the button
    // that opened the form. The button was the only gate before, which made
    // it decorative: any path that put an id in the form wrote through.
    //
    // A create (no id) is not gated here — the object does not exist yet, so
    // there is nothing to hold authority over. GeoLink is what accepts or
    // refuses a new row.
    const editingId = (form.id ?? '').trim();
    if (editingId) {
      const state = formAuthRef.current;
      if (state.loading) {
        setErrorMsg('Still checking what this wallet may do to this object. Try again in a moment.');
        return;
      }
      const row = rowsRef.current.find((r) => r.id === editingId) ?? null;
      const permitted = row
        ? rowMayBeEdited(row, state.auth, walletAddress, isAdmin)
        // Not in the loaded list — decide from the chain alone rather than
        // falling back to provenance we do not have.
        : !!state.auth && (state.auth.canManageObject || state.auth.canManageBinding);
      if (!permitted) {
        setErrorMsg(
          state.error
            ? `Authorization could not be resolved (${state.error}), so the write was not attempted.`
            : rowEditRefusal(state.auth, walletAddress),
        );
        return;
      }
    }

    let body: SpatialObjectAdminCreateBody;
    let parentWarning: string | null = null;
    // Probed rather than assumed: on a deployment that promotes the id
    // without a guard, sending it can cost the whole object.
    const bridgeSafe = await metadataOrganizationBridgeIsSafe();
    try {
      const knownIds = new Set(rowsRef.current.map((r) => r.id));
      parentWarning = resolveParentLink(form, knownIds).warning;
      body = buildCreateBody(form, form.id ? null : walletAddress, knownIds, bridgeSafe);
    } catch (e) {
      setErrorMsg((e as Error).message);
      return;
    }
    setSaving(true);
    try {
      // Capture local tiers/geometry before the API response can strip them
      // from the form — create/update often echoes metadata without `tiers`.
      const localTiers = form.tiers.length > 0 ? [...form.tiers] : [];
      const orgWarning = !bridgeSafe && form.organization_id.trim()
        ? 'The organization link was not saved: this GeoLink deployment does not '
          + 'report a guarded organization bridge, and sending it there can reject '
          + 'the whole object.'
        : null;
      const suffix = [parentWarning, orgWarning].filter(Boolean).map((w) => ` ${w}`).join('');
      let savedId = form.id;
      if (form.id) {
        await geoLinkSpatialObjectsAdminClient.updateObject(form.id, body);
        setStatusMsg(`Updated. Map layer reloaded.${suffix}`);
      } else {
        const created = await geoLinkSpatialObjectsAdminClient.createObject(body);
        savedId = created.id;
        setStatusMsg(`Created ${created.objectType} ${created.id}. Map layer reloaded.${suffix}`);
        // Switch into edit mode, but keep local tiers/geometry when the API
        // omits them — otherwise the form (and next save) lose setbacks.
        setForm((prev) => {
          const fromApi = formFromRow(created);
          return {
            ...fromApi,
            tiers: fromApi.tiers.length > 0 ? fromApi.tiers : prev.tiers,
            geometry: fromApi.geometry.trim() ? fromApi.geometry : prev.geometry,
            height_meters: fromApi.height_meters || prev.height_meters,
            base_height_meters: fromApi.base_height_meters || prev.base_height_meters,
          };
        });
        setEditing(true);
      }
      teardownDraw();
      // Inject stepped geometry into the live map BEFORE clearing the
      // temporary import preview — otherwise closing/saving snaps back to
      // a single-height MultiPolygon from /render.
      if (savedId && body.geometry) {
        window.dispatchEvent(new CustomEvent('geolink:spatial-object-upsert', {
          detail: {
            id: savedId,
            geometry: body.geometry,
            heightMeters: body.height_meters,
            baseHeightMeters: body.base_height_meters,
            tiers: localTiers,
            // Real user save: this geometry+tiers pair came straight from
            // the import and may replace any previously cached pair.
            authoritative: true,
            name: body.name ?? undefined,
            objectType: body.object_type,
            colorKey: typeof body.metadata?.color_key === 'string'
              ? body.metadata.color_key
              : undefined,
          },
        }));
      }
      // Tell the scheduler an occasion was saved or removed. GeoTrust's
      // server never sees this write — the object goes straight to GeoLink
      // with the admin credential — so without this nudge a new event
      // would not be picked up until the next reconcile pass.
      if (savedId) {
        void scheduleSpatialOccasion(savedId, walletAddress).catch(() => {
          /* the periodic reconcile is the backstop */
        });
      }
      clearImportPreview(map);
      void refreshRows();
      // Do not soft-refresh the live map here. Upsert already painted the
      // import tiers; an immediate /render pull reorders polygons and
      // makes Atlantis look wrong the moment Create finishes.
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [form, teardownDraw, refreshRows, walletAddress, map, isAdmin]);

  /**
   * Fly the live map to the object currently in the editor. Averages the
   * geometry's coordinates so the camera centres on the whole footprint
   * (not a single corner) and pitches in for a 3D look.
   */
  const flyToObject = useCallback(() => {
    if (!map) return;
    setErrorMsg(null);
    try {
      const parsed = JSON.parse(form.geometry || '{}') as { coordinates?: unknown };
      let sumLng = 0;
      let sumLat = 0;
      let n = 0;
      const walk = (c: unknown): void => {
        if (Array.isArray(c)) {
          if (typeof c[0] === 'number' && typeof c[1] === 'number') {
            sumLng += c[0]; sumLat += c[1]; n += 1;
          } else c.forEach(walk);
        }
      };
      walk(parsed.coordinates);
      if (n === 0) {
        setErrorMsg('This object has no geometry to fly to yet.');
        return;
      }
      map.flyTo({ center: [sumLng / n, sumLat / n], zoom: 17, pitch: 55, essential: true });
    } catch {
      setErrorMsg('Could not read this object\u2019s geometry.');
    }
  }, [map, form.geometry]);

  const deleteRow = useCallback(async (row: SpatialObjectAdminRow) => {
    setErrorMsg(null);
    if (!walletAddress) {
      setErrorMsg('Connect a wallet before deleting spatial objects.');
      return;
    }

    // Re-checked at the point of deletion. The disabled button was the only
    // gate before, and a disabled button is a hint, not a boundary.
    const auth = authForRowRef.current(row.id);
    if (!rowMayBeEdited(row, auth, walletAddress, isAdmin)) {
      setErrorMsg(rowEditRefusal(auth, walletAddress));
      return;
    }

    if (!window.confirm(`Delete spatial object "${row.name || row.id}"?`)) return;
    try {
      await geoLinkSpatialObjectsAdminClient.deleteObject(row.id);
      setStatusMsg(`Deleted ${row.id}.`);
      if (form.id === row.id) {
        setForm(EMPTY_FORM);
        setEditing(false);
      }
      void refreshRows();
      // Soft refresh cannot drop deleted features from the map accumulator —
      // remove this id from the live layer explicitly.
      window.dispatchEvent(new CustomEvent('geolink:spatial-object-remove', {
        detail: { id: row.id },
      }));
      window.dispatchEvent(new CustomEvent('geolink:spatial-refresh'));
    } catch (e) {
      setErrorMsg((e as Error).message);
    }
  }, [refreshRows, walletAddress, form.id, isAdmin]);

  const deleteRows = useCallback(async (toDelete: SpatialObjectAdminRow[]) => {
    setErrorMsg(null);
    if (!walletAddress) {
      setErrorMsg('Connect a wallet before deleting spatial objects.');
      return;
    }
    if (toDelete.length === 0) return;

    // Filtered rather than refused outright: a bulk selection can be made
    // before authorization for every row has resolved, and dropping the
    // rows this wallet may not touch is more useful than abandoning the
    // whole batch. What is not acceptable is deleting them.
    const authFor = authForRowRef.current;
    const permitted = toDelete.filter((row) => rowMayBeEdited(row, authFor(row.id), walletAddress, isAdmin));
    const refused = toDelete.length - permitted.length;
    if (permitted.length === 0) {
      setErrorMsg(
        `This wallet has no authority over ${refused === 1 ? 'that object' : 'any of those objects'}. `
        + 'Nothing was deleted.',
      );
      return;
    }
    if (!window.confirm(
      `Delete ${permitted.length} spatial object${permitted.length === 1 ? '' : 's'}? This cannot be undone.`
      + (refused > 0
        ? `\n\n${refused} selected object${refused === 1 ? '' : 's'} will be skipped: this wallet `
          + 'has no authority over them.'
        : ''),
    )) return;

    let ok = 0;
    const failures: string[] = [];
    const deletedIds: string[] = [];
    // Sequential so a mid-batch failure doesn't spam the backend and we can
    // report exactly how far we got.
    for (const row of permitted) {
      try {
        await geoLinkSpatialObjectsAdminClient.deleteObject(row.id);
        ok += 1;
        deletedIds.push(row.id);
      } catch (e) {
        failures.push(`${row.name || row.id.slice(0, 8)}: ${(e as Error).message}`);
      }
    }
    setStatusMsg(
      `Deleted ${ok} of ${permitted.length} object${permitted.length === 1 ? '' : 's'}.`
      + (refused > 0 ? ` ${refused} skipped for lack of authority.` : ''),
    );
    if (failures.length) setErrorMsg(`Some deletes failed:\n${failures.join('\n')}`);
    if (form.id && deletedIds.includes(form.id)) {
      setForm(EMPTY_FORM);
      setEditing(false);
    }
    void refreshRows();
    for (const id of deletedIds) {
      window.dispatchEvent(new CustomEvent('geolink:spatial-object-remove', {
        detail: { id },
      }));
    }
    window.dispatchEvent(new CustomEvent('geolink:spatial-refresh'));
  }, [refreshRows, walletAddress, form.id, isAdmin]);

  // ─── Preview tab ────────────────────────────────────────────────────

  const loadPreview = useCallback(async () => {
    setErrorMsg(null);
    try {
      const wantedId = previewObjectId.trim();
      const env = await geoLinkSpatialObjectsAdminClient.previewRender({
        provider: previewProvider,
        objectId: wantedId || undefined,
      });
      // Defensive client-side filter: GeoLink's preview endpoint is
      // SUPPOSED to honour the `objectId` query param, but some
      // backend builds ship the full collection regardless and rely on
      // the caller to pick the right feature. We MUST filter — without
      // this guard, when the server returns the full collection the
      // preview map's fitBounds frames the FIRST feature instead of the
      // one the operator just asked to inspect, which manifests as
      // "the wrong building loaded in preview" / "1 behind the index".
      //
      // Different GeoLink builds have stamped the spatial-object UUID
      // on different keys over time:
      //   • top-level `id` (the GeoJSON canonical place)
      //   • `properties.objectId` (the canonical spatial-objects key)
      //   • `properties.id`       (older builds before objectId was added)
      //   • `properties.spatial_object_id` (one Postgres-backed build)
      // We try all of them so the filter actually matches regardless of
      // backend version.
      if (wantedId && env?.featureCollection?.features) {
        const idCandidates = (f: { id?: string | number; properties?: Record<string, unknown> | null }): string[] => {
          const out: string[] = [];
          if (f.id != null) out.push(String(f.id));
          const p = (f.properties ?? {}) as Record<string, unknown>;
          for (const key of ['objectId', 'object_id', 'id', 'spatial_object_id', 'spatialObjectId']) {
            const v = p[key];
            if (typeof v === 'string' && v) out.push(v);
            else if (typeof v === 'number') out.push(String(v));
          }
          return out;
        };
        const filtered = env.featureCollection.features.filter(f => idCandidates(f).includes(wantedId));
        if (filtered.length > 0) {
          setPreviewEnvelope({
            ...env,
            featureCollection: { ...env.featureCollection, features: filtered },
          });
          return;
        }
        // Object id was provided but no match — DO NOT fall through to
        // the full collection. Showing all objects when the operator
        // asked to inspect a specific one is exactly what produces the
        // "the wrong one is loading" symptom (the preview map's
        // fitBounds frames feature[0], which has no relationship to
        // the row the user clicked Edit on). Surface a clear error
        // and an empty preview instead.
        setPreviewEnvelope(null);
        setErrorMsg(
          `Object id "${wantedId}" was not present in the preview response. ` +
          `The render endpoint returned ${env.featureCollection.features.length} other ` +
          `feature(s); none of them carried this id under any of the known keys. ` +
          `Try clicking ↻ Reload map layer, or open the Objects tab and re-pick the row.`,
        );
        return;
      }
      setPreviewEnvelope(env);
    } catch (e) {
      setErrorMsg((e as Error).message);
      setPreviewEnvelope(null);
    }
  }, [previewProvider, previewObjectId]);

  // Auto-refresh the envelope when the user switches the Preview-tab
  // provider dropdown OR types/changes the Object id. GeoLink's
  // /render endpoint emits provider-specific property aliases (mapbox
  // uses `fill_color`, maptiler uses `render_height`, etc.), so
  // reusing a stale `provider=mapbox` envelope for a maplibre/maptiler
  // engine swap silently breaks the suggested style block; and when
  // the user picks a different Object id, the framing/fitBounds need
  // to land on that object. Only runs while the Preview tab is
  // active.
  useEffect(() => {
    if (tab !== 'preview') return;
    void loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewProvider, previewObjectId]);

  // ─── Tester tab ─────────────────────────────────────────────────────

  const runTester = useCallback(async () => {
    setErrorMsg(null);
    setTesterResult(null);
    if (!testerObjectId.trim()) { setErrorMsg('Pick a spatial object to test.'); return; }
    try {
      const res = await geoLinkSpatialObjectsAdminClient.testInteraction(
        testerObjectId.trim(),
        {
          interaction_type: testerInteraction.trim() || 'tap',
          actor_wallet:     testerActor.trim() || walletAddress || null,
          actor_lng:        playerLocation?.longitude ?? null,
          actor_lat:        playerLocation?.latitude  ?? null,
        },
      );
      setTesterResult(res);
    } catch (e) {
      setErrorMsg((e as Error).message);
    }
  }, [testerObjectId, testerInteraction, testerActor, walletAddress, playerLocation]);

  // ─── Render ─────────────────────────────────────────────────────────

  if (!isOpen) return null;

  const isCentered = openMode === 'centered';

  // ── Picking mode ────────────────────────────────────────────────────
  // While the operator is mid-pick (clicking a 3D building, drawing a
  // polygon, etc.) the full panel UI gets out of the way and we render
  // a thin top-of-viewport status bar instead. This is critical for
  // centered mode (the modal + backdrop otherwise cover the whole
  // map) and a useful affordance in floating mode too — the operator
  // doesn't have to remember which button puts the cursor in
  // "crosshair / picking" state.
  if (pickingMode != null) {
    const cancelPick = () => {
      if (pickingMode === 'import-building') teardownImport();
      else if (pickingMode === 'parent') teardownParentPick();
      else teardownDraw();
    };
    const message =
      pickingMode === 'import-building'
        ? '🏢 Click any 3D building on the map to copy its footprint and height'
        : pickingMode === 'parent'
          ? '🔗 Click the object on the map that should be the parent'
          : '✏️ Click on the map to add vertices — double-click to finish';
    return (
      <PickingPill
        message={message}
        onCancel={cancelPick}
      />
    );
  }

  const panelClass =
    'spatial-admin-panel' +
    (dragging ? ' dragging' : '') +
    (minimized && !isCentered ? ' minimized' : '') +
    (isCentered ? ' centered' : '');

  // Centered mode: render as a viewport-centred modal sheet. We bypass
  // the saved drag position entirely (and the drag handlers below
  // become no-ops) so the menu invocation always lands in the same
  // predictable place regardless of where the floating panel was last
  // dragged to.
  const panelStyle: React.CSSProperties = isCentered
    ? {
        top: '50%',
        left: '50%',
        right: 'auto',
        transform: 'translate(-50%, -50%)',
        width: 'min(960px, 92vw)',
        maxWidth: 'min(960px, 92vw)',
        maxHeight: '88vh',
      }
    : {
        top: pos.y,
        left: pos.x,
        right: 'auto',
        // Always keep the bottom of the panel at least 16px above the
        // viewport bottom so its footer / scrollbar stays reachable
        // wherever the user has dragged it to.
        maxHeight: `calc(100vh - ${pos.y + 16}px)`,
      };

  // Wrap the panel in a backdrop in centered mode so clicking outside
  // dismisses it (mirrors the rest of GeoTrust's modal overlays).
  const panel = (
    <div
      className={panelClass}
      role="dialog"
      aria-label="Spatial Objects admin"
      style={panelStyle}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        ref={headerRef}
        className="spatial-admin-header"
        title="Drag to move"
      >
        <span className="spatial-admin-header-grip" aria-hidden="true">⋮⋮</span>
        <div className="spatial-admin-header-title">
          <h2>🏙️ Spatial objects</h2>
        </div>
        <div className="spatial-admin-header-actions">
          <button
            className="spatial-admin-iconbtn"
            aria-label="Reset position"
            title="Reset position"
            onClick={resetPosition}
          >⤢</button>
          <button
            className="spatial-admin-iconbtn"
            aria-label={minimized ? 'Restore panel' : 'Minimise panel'}
            title={minimized ? 'Restore' : 'Minimise'}
            onClick={() => setMinimized(m => !m)}
          >{minimized ? '▢' : '—'}</button>
          <button
            className="spatial-admin-iconbtn spatial-admin-close"
            aria-label="Close"
            title="Close"
            onClick={onClose}
          >×</button>
        </div>
      </div>

      <div className="spatial-admin-tabs">
        <button className={`spatial-admin-tab ${tab === 'objects' ? 'active' : ''}`} onClick={() => setTab('objects')}>Objects</button>
        <button className={`spatial-admin-tab ${tab === 'editor' ? 'active' : ''}`}  onClick={() => setTab('editor')}>{editing ? 'Edit' : 'New'}</button>
        <button
          className={`spatial-admin-tab ${tab === 'preview' ? 'active' : ''}`}
          onClick={() => {
            setTab('preview');
            // Always re-sync the preview Object id with the currently
            // editing row when one is open — overwrites any stale id
            // left over from a previous Preview session. Without this
            // overwrite, editing the Las Vegas Sphere would still
            // pre-fill with the Burj Khalifa id from the last preview
            // run, and the auto-fit logic would frame the wrong
            // building.
            if (editing && form.id) {
              if (previewObjectId !== form.id) {
                setPreviewObjectId(form.id);
                // The provider/objectId effect below will fire the
                // refetch — no need to call loadPreview() here.
              } else if (!previewEnvelope) {
                void loadPreview();
              }
            } else if (!previewEnvelope) {
              void loadPreview();
            }
          }}
        >Preview</button>
        <button className={`spatial-admin-tab ${tab === 'tester' ? 'active' : ''}`}  onClick={() => setTab('tester')}>Test</button>
      </div>

      <div className="spatial-admin-body">

        {!walletAddress && (
          <div className="spatial-admin-key-error">
            <strong>Wallet not connected.</strong> You can browse and preview
            spatial objects, but creating, editing, or deleting is disabled
            until a wallet is connected. Connect a wallet from the main menu
            to manage objects.
          </div>
        )}

        {errorMsg && <div className="spatial-admin-status error">⚠ {errorMsg}</div>}
        {statusMsg && (
          <div className="spatial-admin-status ok" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1 }}>✓ {statusMsg}</span>
            <button
              type="button"
              className="spatial-admin-btn"
              title="Clear the live map layer cache and re-fetch from /spatial-objects/render"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('geolink:spatial-refresh', {
                  detail: { hard: true },
                }));
                setStatusMsg('Map layer reload triggered.');
              }}
            >
              ↻ Reload map layer
            </button>
          </div>
        )}

        {tab === 'objects' && (
          <ObjectsTab
            rows={rows}
            visible={visibleRows}
            totalCount={totalCount}
            serverScoped={serverScoped}
            inViewCount={inViewCount}
            hasViewport={!!viewport.bounds}
            loading={loading}
            filterType={filterType}
            setFilterType={setFilterType}
            filterOwner={filterOwner}
            setFilterOwner={setFilterOwner}
            mineOnly={mineOnly}
            setMineOnly={setMineOnly}
            listSearch={listSearch}
            setListSearch={setListSearch}
            filterCountry={filterCountry}
            setFilterCountry={setFilterCountry}
            countries={availableCountries}
            inViewOnly={inViewOnly}
            setInViewOnly={setInViewOnly}
            sortKey={sortKey}
            setSortKey={setSortKey}
            walletAddress={walletAddress}
            isAdmin={isAdmin}
            rowAuthorizations={rowAuthorizations}
            onRefresh={refreshRows}
            onNew={startNew}
            onEdit={startEdit}
            onDelete={deleteRow}
            onBulkDelete={deleteRows}
            onAddChild={startChild}
            onZoomTo={zoomToRow}
            onTest={(row) => { setTesterObjectId(row.id); setTab('tester'); }}
          />
        )}

        {tab === 'editor' && (
          <EditorTab
            form={form}
            setForm={setForm}
            rules={rules}
            walletAddress={walletAddress}
            onRuleChange={onRuleChange}
            startDraw={startDraw}
            stopDraw={teardownDraw}
            startImport={startImportFrom3DBuilding}
            importOffer={importOffer}
            importPicked={importPicked}
            onTogglePicked={toggleImportPicked}
            onApplyPicked={applyImportPicked}
            onDismissOffer={dismissImportOffer}
            usePlayer={usePlayerLocation}
            useMapCenter={useMapCenter}
            importVector={importVector}
            search={search}
            setSearch={setSearch}
            flyToAddress={flyToAddress}
            mapboxToken={mapboxToken}
            parentCandidates={editorParentCandidates}
            selectedParent={selectedParentRow}
            startPickParent={startPickParent}
            canPickParentOnMap={!!map}
            countryIso2={editingCountryIso2}
            signer={signer}
            networkPassphrase={networkPassphrase}
            isAdmin={isAdmin}
            formAuthorization={formAuthorization}
          />
        )}

        {tab === 'preview' && (
          <PreviewTab
            previewProvider={previewProvider}
            setPreviewProvider={setPreviewProvider}
            previewObjectId={previewObjectId}
            setPreviewObjectId={setPreviewObjectId}
            previewEnvelope={previewEnvelope}
            onLoad={loadPreview}
          />
        )}

        {tab === 'tester' && (
          <TesterTab
            rows={rows}
            agents={agents}
            walletAddress={walletAddress}
            objectId={testerObjectId}
            setObjectId={setTesterObjectId}
            actor={testerActor}
            setActor={setTesterActor}
            interaction={testerInteraction}
            setInteraction={setTesterInteraction}
            result={testerResult}
            onRun={runTester}
          />
        )}
      </div>

      {tab === 'editor' && (
        <div className="spatial-admin-footer">
          <button className="spatial-admin-button ghost" onClick={() => { setForm(EMPTY_FORM); setEditing(false); teardownDraw(); }} disabled={saving}>Reset</button>
          <button className="spatial-admin-button" onClick={() => setTab('objects')} disabled={saving}>Back</button>
          <button
            className="spatial-admin-button"
            onClick={flyToObject}
            disabled={!map || !form.geometry.trim()}
            title={!map ? 'Map not ready' : !form.geometry.trim() ? 'No geometry to fly to' : 'Fly the map to this object'}
          >
            📍 Fly to
          </button>
          <button
            className="spatial-admin-button primary"
            onClick={saveForm}
            disabled={!walletAddress || saving}
            title={!walletAddress ? 'Connect a wallet to save spatial objects' : ''}
          >
            {saving
              ? (editing ? 'Saving…' : 'Creating…')
              : (editing ? 'Save changes' : 'Create object')}
          </button>
        </div>
      )}
    </div>
  );

  // In centered mode, wrap the panel in a clickable backdrop so the
  // operator can dismiss with an outside click (matches the rest of
  // GeoTrust's modal overlays). Floating mode keeps the bare panel —
  // a backdrop there would block the live map.
  if (!isCentered) return panel;
  return (
    <div
      className="spatial-admin-backdrop"
      role="presentation"
      onClick={onClose}
    >
      {panel}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════
//  Sub-components
// ═════════════════════════════════════════════════════════════════════

/**
 * Slim status pill rendered while the panel is waiting for the operator
 * to click on the map (import 3D building / draw geometry). Sits at the
 * top centre of the viewport, leaves the rest of the screen clear so
 * the live map underneath is fully reachable. Esc cancels.
 */
const PickingPill: React.FC<{ message: string; onCancel: () => void }> = ({ message, onCancel }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="spatial-admin-pickbar" role="status" aria-live="polite">
      <span className="spatial-admin-pickbar-dot" />
      <span className="spatial-admin-pickbar-msg">{message}</span>
      <button
        type="button"
        className="spatial-admin-pickbar-cancel"
        onClick={onCancel}
        title="Cancel (Esc)"
      >Cancel</button>
    </div>
  );
};

/** Compact "3d ago" / "just now" for the list rows. */
function relativeTime(iso: string | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const secs = Math.max(0, (Date.now() - t) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86_400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 2_592_000) return `${Math.floor(secs / 86_400)}d ago`;
  return new Date(t).toLocaleDateString();
}

const shortWallet = (w: string | null | undefined): string =>
  w && w.length > 10 ? `${w.slice(0, 4)}…${w.slice(-4)}` : (w || '—');

const SORT_OPTIONS: { id: SpatialSortKey; label: string }[] = [
  { id: 'view',     label: 'In view first' },
  { id: 'distance', label: 'Nearest' },
  { id: 'name',     label: 'Name A–Z' },
  { id: 'type',     label: 'Type' },
  { id: 'updated',  label: 'Recently updated' },
];

interface ObjectsTabProps {
  /** Every loaded row — drives child counts and orphan detection. */
  rows: SpatialObjectAdminRow[];
  /** Filtered + sorted view of {@link rows}. */
  visible: DecoratedRow[];
  /** Unpaged match count from GeoLink. Null when the build predates it. */
  totalCount: number | null;
  /** True when GeoLink returned a viewport-scoped page. */
  serverScoped: boolean;
  inViewCount: number;
  hasViewport: boolean;
  loading: boolean;
  filterType: SpatialObjectType | '';
  setFilterType: (v: SpatialObjectType | '') => void;
  filterOwner: string;
  setFilterOwner: (v: string) => void;
  mineOnly: boolean;
  setMineOnly: (v: boolean) => void;
  listSearch: string;
  setListSearch: (v: string) => void;
  filterCountry: string;
  setFilterCountry: (v: string) => void;
  countries: { iso2: string; name: string; count: number }[];
  inViewOnly: boolean;
  setInViewOnly: (v: boolean) => void;
  sortKey: SpatialSortKey;
  setSortKey: (v: SpatialSortKey) => void;
  walletAddress: string | null;
  isAdmin: boolean;
  /** Contract-backed authority for the listed rows. See `useObjectAuthorizations`. */
  rowAuthorizations: ObjectAuthorizationMapState;
  onRefresh: () => void;
  onNew: (tplId?: string) => void;
  onEdit: (row: SpatialObjectAdminRow) => void;
  onDelete: (row: SpatialObjectAdminRow) => void;
  onBulkDelete: (rows: SpatialObjectAdminRow[]) => void;
  onTest: (row: SpatialObjectAdminRow) => void;
  /** Start a new child object under `row`. */
  onAddChild: (row: SpatialObjectAdminRow) => void;
  /** Fly the live map to the row. */
  onZoomTo: (d: DecoratedRow) => void;
}

const ObjectsTab: React.FC<ObjectsTabProps> = (p) => {
  const canEdit = !!p.walletAddress;
  const lockTip = canEdit ? '' : 'Connect a wallet to enable';

  const authFor = p.rowAuthorizations.authFor;

  // Which rows this wallet may edit or delete, from the chain rather than
  // from who created the GeoLink row.
  const ownsRow = useCallback(
    (r: SpatialObjectAdminRow) => rowMayBeEdited(r, authFor(r.id), p.walletAddress, p.isAdmin),
    [authFor, p.walletAddress, p.isAdmin],
  );

  /**
   * Whether a child may be created under a row.
   *
   * Its own permission, not a synonym for editing. Registering a child in
   * the spatial registry is country-scoped, so an organization operator who
   * may run a building cannot invent new floors under it. Until the row is
   * on chain there is nothing to be scoped against, so it follows the edit
   * gate instead.
   */
  const mayAddChild = useCallback(
    (r: SpatialObjectAdminRow) => {
      const auth = authFor(r.id);
      if (auth?.registered) return auth.canCreateChildObject;
      return rowMayBeEdited(r, auth, p.walletAddress, p.isAdmin);
    },
    [authFor, p.walletAddress, p.isAdmin],
  );

  const rowTip = useCallback(
    (r: SpatialObjectAdminRow) => rowEditRefusal(authFor(r.id), p.walletAddress),
    [authFor, p.walletAddress],
  );

  // Bulk selection (ids of rows ticked for deletion).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Parents the operator has folded shut. Default is expanded so a
  // freshly opened panel shows the whole hierarchy at a glance.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleCollapsed = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Drop ids that are no longer present after a refresh/filter change.
  useEffect(() => {
    setSelected((prev) => {
      const present = new Set(p.rows.map((r) => r.id));
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => { if (present.has(id)) next.add(id); else changed = true; });
      return changed ? next : prev;
    });
  }, [p.rows]);

  const visibleRowList = useMemo(() => p.visible.map((d) => d.row), [p.visible]);
  const deletableRows = useMemo(() => visibleRowList.filter(ownsRow), [visibleRowList, ownsRow]);
  const selectedRows = useMemo(() => p.rows.filter((r) => selected.has(r.id)), [p.rows, selected]);
  const allDeletableSelected = deletableRows.length > 0 && deletableRows.every((r) => selected.has(r.id));

  // Nest the visible rows, then flatten back out honouring collapse
  // state so the table body stays a flat <tr> list with indentation.
  const tree = useMemo(() => buildTree(p.visible, p.rows), [p.visible, p.rows]);
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of p.rows) m.set(r.id, r.name || r.id.slice(0, 8));
    return m;
  }, [p.rows]);

  const flatRows = useMemo(() => {
    const out: { d: DecoratedRow; depth: number; hidesChildren: boolean }[] = [];
    const walk = (nodes: DecoratedRow[], depth: number) => {
      for (const d of nodes) {
        const kids = tree.childrenByParent.get(d.row.id);
        const isCollapsed = collapsed.has(d.row.id);
        out.push({ d, depth, hidesChildren: !!kids?.length && isCollapsed });
        if (kids?.length && !isCollapsed) walk(kids, depth + 1);
      }
    };
    walk(tree.roots, 0);
    return out;
  }, [tree, collapsed]);

  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (deletableRows.length > 0 && deletableRows.every((r) => prev.has(r.id))) return new Set();
      return new Set(deletableRows.map((r) => r.id));
    });
  }, [deletableRows]);

  return (
  <>
    <div className="spatial-admin-section">
      <h3>Quick add</h3>
      <div className="spatial-admin-template-row">
        {QUICK_TEMPLATES.map(t => (
          <button
            key={t.id}
            className="spatial-admin-button"
            onClick={() => p.onNew(t.id)}
            disabled={!canEdit}
            title={lockTip}
          >
            <span style={{ fontSize: 18 }}>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>
      <div className="spatial-admin-quickbar">
        <button
          className="spatial-admin-button primary"
          onClick={() => p.onNew()}
          disabled={!canEdit}
          title={lockTip}
        >+ New</button>
        <button className="spatial-admin-button" onClick={p.onRefresh}>↻ Refresh</button>
      </div>
    </div>

    <div className="spatial-admin-section">
      <h3>Find</h3>
      <div className="spatial-admin-row">
        <input
          className="spatial-admin-input"
          value={p.listSearch}
          onChange={(e) => p.setListSearch(e.target.value)}
          placeholder="Search name, id, type, owner, parent, country…"
        />
      </div>
      <div className="spatial-admin-row" style={{ flexDirection: 'row', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <label>Sort</label>
          <select
            className="spatial-admin-select"
            value={p.sortKey}
            onChange={(e) => p.setSortKey(e.target.value as SpatialSortKey)}
          >
            {SORT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label>Country</label>
          <select
            className="spatial-admin-select"
            value={p.filterCountry}
            onChange={(e) => p.setFilterCountry(e.target.value)}
            disabled={p.countries.length === 0}
            title={p.countries.length === 0 ? 'No countries resolved for the loaded objects yet' : ''}
          >
            <option value="">All countries</option>
            {p.countries.map((c) => (
              <option key={c.iso2} value={c.iso2}>{c.name} ({c.count})</option>
            ))}
          </select>
        </div>
      </div>
      <div className="spatial-admin-row" style={{ flexDirection: 'row', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <label>Type</label>
          <select className="spatial-admin-select" value={p.filterType} onChange={(e) => p.setFilterType(e.target.value as SpatialObjectType | '')}>
            <option value="">All types</option>
            {OBJECT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label>Owner wallet</label>
          <input className="spatial-admin-input" value={p.filterOwner} onChange={(e) => p.setFilterOwner(e.target.value)} placeholder="G… or C…" disabled={p.mineOnly} />
        </div>
      </div>
      <div className="spatial-admin-row" style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'none', letterSpacing: 0 }}>
          <input
            type="checkbox"
            checked={p.inViewOnly}
            onChange={(e) => p.setInViewOnly(e.target.checked)}
            disabled={!p.hasViewport}
            title={p.hasViewport ? 'Only objects inside the current map view' : 'Map viewport not available'}
          />
          In view only ({p.inViewCount})
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'none', letterSpacing: 0 }}>
          <input type="checkbox" checked={p.mineOnly} onChange={(e) => p.setMineOnly(e.target.checked)} disabled={!p.walletAddress} />
          Mine only ({p.walletAddress ? shortWallet(p.walletAddress) : 'no wallet'})
        </label>
      </div>
    </div>

    <div className="spatial-admin-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <h3 style={{ margin: 0 }}>
          Objects
          {' '}
          <span style={{ fontWeight: 400, opacity: 0.75 }}>
            (showing {p.visible.length} of {p.totalCount ?? p.rows.length}
            {p.inViewOnly ? ', in view' : ''})
          </span>
        </h3>
        {selected.size > 0 && (
          <button
            className="spatial-admin-button danger"
            onClick={() => { p.onBulkDelete(selectedRows); }}
            disabled={!canEdit}
            title={canEdit ? `Delete the ${selected.size} selected object(s)` : lockTip}
            style={{ padding: '4px 10px' }}
          >🗑 Delete selected ({selected.size})</button>
        )}
      </div>
      {p.serverScoped && (
        <div className="spatial-admin-helper">
          {p.totalCount} objects match. GeoLink is filtering to the current map
          view — pan or zoom to load a different area.
        </div>
      )}
      {!p.serverScoped && p.rows.length >= 1000 && (
        <div className="spatial-admin-helper" style={{ color: '#fbbf24' }}>
          GeoLink returned the 1000-row cap — objects beyond it are not loaded and
          cannot be searched. Narrow the type / owner filter, or see the pagination
          request in docs/geolink-spatial-admin-and-organizations.md.
        </div>
      )}
      {p.loading
        ? <div className="spatial-admin-empty">Loading…</div>
        : p.visible.length === 0
          ? (
            <div className="spatial-admin-empty">
              {p.rows.length === 0
                ? 'No spatial objects yet.'
                : 'No objects match the current search and filters.'}
            </div>
          )
          : (
          <table className="spatial-admin-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  <input
                    type="checkbox"
                    checked={allDeletableSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = selected.size > 0 && !allDeletableSelected;
                    }}
                    onChange={toggleAll}
                    disabled={!canEdit || deletableRows.length === 0}
                    title="Select all visible rows you can delete"
                  />
                </th>
                <th>Name</th>
                <th>Type</th>
                <th style={{ width: 26 }} title="Country">🌐</th>
                <th>Owner</th>
                <th>Vis</th>
                <th>Rule</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {flatRows.map(({ d, depth, hidesChildren }) => (
                <ObjectRow
                  key={d.row.id}
                  d={d}
                  depth={depth}
                  hidesChildren={hidesChildren}
                  hasChildren={(tree.childrenByParent.get(d.row.id)?.length ?? 0) > 0}
                  parentName={
                    depth === 0 && d.row.parentObjectId
                      ? nameById.get(d.row.parentObjectId) ?? null
                      : null
                  }
                  owns={ownsRow(d.row)}
                  mayAddChild={mayAddChild(d.row)}
                  authorityTip={rowTip(d.row)}
                  canEdit={canEdit}
                  lockTip={lockTip}
                  checked={selected.has(d.row.id)}
                  onToggle={toggleOne}
                  onToggleCollapsed={toggleCollapsed}
                  onEdit={p.onEdit}
                  onDelete={p.onDelete}
                  onTest={p.onTest}
                  onAddChild={p.onAddChild}
                  onZoomTo={p.onZoomTo}
                />
              ))}
              {tree.orphans.length > 0 && (
                <>
                  <tr>
                    <td colSpan={8} style={{ paddingTop: 10 }}>
                      <div
                        style={{ fontSize: 11, color: '#fbbf24', fontWeight: 600 }}
                        title="These objects reference a parent that is not in the loaded set — either it was deleted, or it is outside the current type / owner filter."
                      >
                        Unlinked children ({tree.orphans.length})
                      </div>
                    </td>
                  </tr>
                  {tree.orphans.map((d) => (
                    <ObjectRow
                      key={d.row.id}
                      d={d}
                      depth={1}
                      hidesChildren={false}
                      hasChildren={false}
                      parentName={null}
                      orphanParentId={d.row.parentObjectId ?? undefined}
                      owns={ownsRow(d.row)}
                      mayAddChild={mayAddChild(d.row)}
                      authorityTip={rowTip(d.row)}
                      canEdit={canEdit}
                      lockTip={lockTip}
                      checked={selected.has(d.row.id)}
                      onToggle={toggleOne}
                      onToggleCollapsed={toggleCollapsed}
                      onEdit={p.onEdit}
                      onDelete={p.onDelete}
                      onTest={p.onTest}
                      onAddChild={p.onAddChild}
                      onZoomTo={p.onZoomTo}
                    />
                  ))}
                </>
              )}
            </tbody>
          </table>
        )}
    </div>
  </>
  );
};

interface ObjectRowProps {
  d: DecoratedRow;
  depth: number;
  hasChildren: boolean;
  hidesChildren: boolean;
  /** Set when a child is shown at top level because its parent was filtered out. */
  parentName: string | null;
  /** Set for rows in the "Unlinked children" group. */
  orphanParentId?: string;
  owns: boolean;
  canEdit: boolean;
  lockTip: string;
  checked: boolean;
  onToggle: (id: string) => void;
  onToggleCollapsed: (id: string) => void;
  onEdit: (row: SpatialObjectAdminRow) => void;
  onDelete: (row: SpatialObjectAdminRow) => void;
  onTest: (row: SpatialObjectAdminRow) => void;
  onAddChild: (row: SpatialObjectAdminRow) => void;
  onZoomTo: (d: DecoratedRow) => void;
  /** Its own permission: creating a child is country-scoped, editing is not. */
  mayAddChild: boolean;
  /** Why authority was refused, for the disabled controls. */
  authorityTip: string;
}

const ObjectRow: React.FC<ObjectRowProps> = ({
  d, depth, hasChildren, hidesChildren, parentName, orphanParentId,
  owns, mayAddChild, authorityTip, canEdit, lockTip, checked,
  onToggle, onToggleCollapsed, onEdit, onDelete, onTest, onAddChild, onZoomTo,
}) => {
  const r = d.row;
  // The specific reason, not "owned by another wallet" — which was often
  // untrue and never actionable. It may now be a suspended object, an
  // unapproved binding, or an organization whose authority was withdrawn.
  const ownerTip = owns ? '' : authorityTip;
  const flag = d.countryIso2 ? flagImgProps(d.countryIso2, 16) : null;
  const updated = relativeTime(r.updatedAt);

  return (
    <tr style={depth > 0 ? { background: 'rgba(255,255,255,0.02)' } : undefined}>
      <td>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(r.id)}
          disabled={!canEdit || !owns}
          title={owns ? 'Select for bulk delete' : ownerTip}
        />
      </td>
      <td title={`${r.id}${updated ? ` · updated ${updated}` : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: depth * 12 }}>
          {hasChildren ? (
            <button
              type="button"
              onClick={() => onToggleCollapsed(r.id)}
              title={hidesChildren ? 'Expand children' : 'Collapse children'}
              style={{
                background: 'none', border: 'none', color: 'inherit', cursor: 'pointer',
                padding: 0, width: 12, lineHeight: 1, fontSize: 10,
              }}
            >{hidesChildren ? '▶' : '▼'}</button>
          ) : (
            <span style={{ width: 12, display: 'inline-block' }} />
          )}
          <span style={{ fontWeight: 600 }}>{r.name || r.id.slice(0, 8)}</span>
          {!owns && <span title={ownerTip} style={{ opacity: 0.7 }}>🔒</span>}
          {d.childCount > 0 && (
            <span className="spatial-admin-pill muted" title={`${d.childCount} child object(s)`}>
              {d.childCount}
            </span>
          )}
        </div>
        <div
          style={{
            color: '#9ca3af', fontSize: 10, paddingLeft: depth * 12 + 16,
            display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
          }}
        >
          <span style={{ fontFamily: 'ui-monospace, monospace' }}>{r.id.slice(0, 8)}</span>
          {d.inView && <span style={{ color: '#34d399' }}>in view</span>}
          {d.distanceM != null && <span>{formatDistance(d.distanceM)}</span>}
          {updated && <span>{updated}</span>}
          {parentName && <span title={r.parentObjectId ?? ''}>child of {parentName}</span>}
          {orphanParentId && (
            <span style={{ color: '#fbbf24' }} title={orphanParentId}>
              missing parent {orphanParentId.slice(0, 8)}
            </span>
          )}
        </div>
      </td>
      <td><span className="spatial-admin-pill muted" title={r.objectType}>{objectTypeLabel(r.objectType)}</span></td>
      <td title={d.countryName ?? 'Country unknown'}>
        {flag
          ? <img {...flag} alt={d.countryIso2 ?? ''} />
          : <span style={{ color: '#9ca3af' }}>—</span>}
      </td>
      <td
        title={r.ownerWallet ?? 'No owner wallet'}
        style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10 }}
      >{shortWallet(r.ownerWallet)}</td>
      <td><span className={`spatial-admin-pill ${r.visibility === 'public' ? 'ok' : 'warn'}`}>{r.visibility ?? '—'}</span></td>
      <td>{r.linkedExecutionRuleId != null ? <span className="spatial-admin-pill">#{String(r.linkedExecutionRuleId)}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
      <td className="row-actions">
        <button
          onClick={() => onZoomTo(d)}
          title={d.point ? 'Zoom the map to this object' : 'No location available'}
          disabled={!d.point}
        >🎯</button>
        <button
          onClick={() => onEdit(r)}
          title={!canEdit ? lockTip : owns ? 'Edit' : ownerTip}
          disabled={!canEdit || !owns}
        >✎</button>
        <button
          onClick={() => onAddChild(r)}
          title={
            !canEdit ? lockTip
              : mayAddChild ? `Add a child object inside ${r.name || 'this object'}`
              : 'Registering a child object is country-scoped, and this wallet is not '
                + 'the admin for this object\'s country.'
          }
          disabled={!canEdit || !mayAddChild}
        >＋</button>
        <button onClick={() => onTest(r)} title="Test interaction">🧪</button>
        <button
          className="danger"
          onClick={() => onDelete(r)}
          title={!canEdit ? lockTip : owns ? 'Delete' : ownerTip}
          disabled={!canEdit || !owns}
        >🗑</button>
      </td>
    </tr>
  );
};

/**
 * The occasion this object hosts.
 *
 * Offered on every object type rather than only `event_object`, because
 * an occasion usually belongs to a place that already exists — the tower
 * that lights up, the venue that opens — and forcing operators to create
 * a second marker beside it is how events end up unattached to anything.
 *
 * A title alone is a valid event. The times are what let the scheduler
 * run it unattended, and are optional so an undated "what's on here" note
 * is still expressible.
 */
const EventFields: React.FC<{
  form: FormState;
  set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}> = ({ form, set }) => {
  const startIso = localInputToIso(form.event_starts_at);
  const endIso = localInputToIso(form.event_ends_at);
  const backwards = !!startIso && !!endIso && new Date(endIso) <= new Date(startIso);

  return (
    <details className="spatial-admin-row" open={!!form.event_title.trim()}>
      <summary style={{ cursor: 'pointer' }}>
        🎉 Event
        {form.event_title.trim() ? ` — ${form.event_title.trim()}` : ''}
      </summary>

      <label>Title</label>
      <input
        className="spatial-admin-input"
        value={form.event_title}
        placeholder="e.g. National Day"
        onChange={(e) => set('event_title', e.target.value)}
      />

      <div className="spatial-admin-row" style={{ flexDirection: 'row' }}>
        <div style={{ flex: 1 }}>
          <label>Starts</label>
          <input
            className="spatial-admin-input"
            type="datetime-local"
            value={form.event_starts_at}
            onChange={(e) => set('event_starts_at', e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label>Ends</label>
          <input
            className="spatial-admin-input"
            type="datetime-local"
            value={form.event_ends_at}
            onChange={(e) => set('event_ends_at', e.target.value)}
          />
        </div>
      </div>

      <label>Venue</label>
      <input
        className="spatial-admin-input"
        value={form.event_venue}
        placeholder="(optional)"
        onChange={(e) => set('event_venue', e.target.value)}
      />

      <label>Ticket contract</label>
      <input
        className="spatial-admin-input"
        value={form.event_ticket_contract}
        placeholder="(optional)"
        onChange={(e) => set('event_ticket_contract', e.target.value)}
      />

      {backwards && (
        <div style={{ fontSize: 11, color: '#fca5a5', marginTop: 4 }}>
          The end is at or before the start, so the occasion would end the
          moment it began.
        </div>
      )}
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
        Times are entered in your own timezone and stored as instants. With
        both set, the surface, broadcast and speech configured under presence
        run automatically at the start and stop at the end.
      </div>
    </details>
  );
};

interface EditorTabProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  rules: ContractExecutionRuleSummary[];
  walletAddress: string | null;
  onRuleChange: (idStr: string) => void;
  startDraw: (mode: 'draw_polygon' | 'draw_point' | 'draw_line_string') => void;
  stopDraw: () => void;
  startImport: () => void;
  /**
   * Structures OSM offered for the last click, when there was a choice to
   * make, with the ids currently ticked. `null` for a plain building.
   */
  importOffer: {
    structures: OsmStructureOption[];
    sourceLabel: string;
    clickLng: number;
    clickLat: number;
  } | null;
  importPicked: number[];
  onTogglePicked: (id: number) => void;
  onApplyPicked: () => void;
  onDismissOffer: () => void;
  usePlayer: () => void;
  /** Snap geometry to the live map's centre. Used to fix mis-located objects
   *  ("burj khalifa" was saved as a Kansas point because the original quick-add
   *  defaulted to the country centroid). */
  useMapCenter: () => void;
  /** Import geometry from a GeoJSON / KML / GPX / zipped Shapefile file. */
  importVector: (file: File) => void;
  search: string;
  setSearch: (s: string) => void;
  flyToAddress: () => void;
  mapboxToken?: string | null;
  /** Loaded objects eligible to parent the object being edited, nearest first. */
  parentCandidates: DecoratedRow[];
  /** The currently linked parent, when it is in the loaded set. */
  selectedParent: SpatialObjectAdminRow | null;
  startPickParent: () => void;
  canPickParentOnMap: boolean;
  /** ISO 3166-1 alpha-2 of the object being edited, when it is known. */
  countryIso2: string | null;
  /** Signs on-chain binding writes. See the overlay's prop of the same name. */
  signer: SpatialSigner | null;
  networkPassphrase?: string;
  /** Protocol admin. Opens the first bind on an object nobody holds yet. */
  isAdmin: boolean;
  /**
   * Contract-backed authority for the object being edited. Resolved once in
   * the overlay so the authority panel and `saveForm` cannot disagree about
   * whether this wallet may write.
   */
  formAuthorization: AuthorizationState<ObjectAuthorization>;
}

/**
 * Parent link editor. Replaces the old free-text uuid box: floors and
 * slices cannot be saved without a parent, and every candidate is drawn
 * from rows the panel already loaded, so the id is always real.
 */
const ParentPicker: React.FC<{
  form: FormState;
  candidates: DecoratedRow[];
  selectedParent: SpatialObjectAdminRow | null;
  onChange: (id: string) => void;
  onPickOnMap: () => void;
  canPickOnMap: boolean;
}> = ({ form, candidates, selectedParent, onChange, onPickOnMap, canPickOnMap }) => {
  const [query, setQuery] = useState('');
  const requirement = parentRequirement(form.object_type);
  const value = form.parent_object_id.trim();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? candidates.filter((d) =>
          (d.row.name ?? '').toLowerCase().includes(q) ||
          d.row.id.toLowerCase().startsWith(q) ||
          objectTypeLabel(d.row.objectType).toLowerCase().includes(q))
      : candidates;
    return base.slice(0, 200);
  }, [candidates, query]);

  // A saved link can point at a row the current filter or search hides.
  // Keep it selectable so opening the editor never silently drops it.
  const valueMissingFromList = !!value && !filtered.some((d) => d.row.id === value);

  const invalid = value !== '' && !isUuid(value);
  const missingRequired = requirement === 'required' && value === '';

  return (
    <div className="spatial-admin-row">
      <label>
        Parent object
        {requirement === 'required' && <span style={{ color: '#f87171' }}> *</span>}
        {requirement === 'recommended' && <span style={{ opacity: 0.6 }}> (recommended)</span>}
      </label>
      {candidates.length > 8 && (
        <input
          className="spatial-admin-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${candidates.length} candidate parents…`}
          style={{ marginBottom: 4 }}
        />
      )}
      <select
        className="spatial-admin-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">
          {requirement === 'required' ? '— Select a parent —' : '— None —'}
        </option>
        {valueMissingFromList && (
          <option value={value}>
            {selectedParent
              ? `${selectedParent.name || selectedParent.id.slice(0, 8)} · ${objectTypeLabel(selectedParent.objectType)}`
              : `${value.slice(0, 8)}… (not in loaded set)`}
          </option>
        )}
        {filtered.map((d) => {
          const dist = formatDistance(d.distanceM);
          return (
            <option key={d.row.id} value={d.row.id}>
              {d.row.name || d.row.id.slice(0, 8)}
              {' · '}{objectTypeLabel(d.row.objectType)}
              {' · '}{d.row.id.slice(0, 8)}
              {dist ? ` · ${dist}` : ''}
              {d.inView ? ' · in view' : ''}
            </option>
          );
        })}
      </select>
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <button
          type="button"
          className="spatial-admin-button"
          onClick={onPickOnMap}
          disabled={!canPickOnMap}
          title={canPickOnMap ? 'Click the parent on the live map' : 'Map not available'}
        >🔗 Pick on map</button>
        <button
          type="button"
          className="spatial-admin-button"
          onClick={() => onChange('')}
          disabled={value === ''}
        >Clear</button>
      </div>
      {missingRequired && (
        <div className="spatial-admin-helper" style={{ color: '#f87171' }}>
          A {objectTypeLabel(form.object_type).toLowerCase()} must sit inside a parent
          {(PARENT_TYPES_FOR[form.object_type] ?? []).length > 0 && (
            <> ({(PARENT_TYPES_FOR[form.object_type] ?? []).map(objectTypeLabel).join(' or ')})</>
          )}.
        </div>
      )}
      {invalid && (
        <div className="spatial-admin-helper" style={{ color: '#f87171' }}>
          Not a UUID — GeoLink will reject this. Pick a parent from the list.
        </div>
      )}
      {!missingRequired && !invalid && candidates.length === 0 && (
        <div className="spatial-admin-helper">
          No loaded objects can parent a {objectTypeLabel(form.object_type).toLowerCase()}.
          Create the parent first, or clear the type filter on the Objects tab.
        </div>
      )}
    </div>
  );
};

/**
 * Owning-organization link.
 *
 * The list comes from GeoLink's organization mirror where one exists
 * and this browser's directory otherwise (see organizationService — the
 * registry itself has no on-chain index), so an id typed in by hand is
 * always allowed and is resolved against the chain when it looks
 * well-formed. That keeps an operator from being blocked just because
 * their browser has not seen the organization before.
 */
const OrganizationPicker: React.FC<{
  value: string;
  onChange: (id: string) => void;
}> = ({ value, onChange }) => {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [resolved, setResolved] = useState<Organization | null>(null);
  const [bridgeSafe, setBridgeSafe] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getOrgConfig().then(async (cfg) => {
      if (cancelled) return;
      setConfigured(cfg.configured);
      if (!cfg.configured) return;
      const result = await listOrganizations();
      if (!cancelled) setOrgs(result.organizations);
    });
    // Warns before the operator picks, rather than after the save
    // silently drops the binding.
    metadataOrganizationBridgeIsSafe().then((safe) => {
      if (!cancelled) setBridgeSafe(safe);
    });
    return () => { cancelled = true; };
  }, []);

  // Resolve an id that isn't in the local directory so the operator sees
  // the organization's real name and status instead of a bare hash.
  const id = value.trim().toLowerCase();
  const inList = orgs.some((o) => o.id === id);
  useEffect(() => {
    if (!id || inList || !isOrgId(id)) { setResolved(null); return; }
    let cancelled = false;
    getOrganization(id).then((org) => { if (!cancelled) setResolved(org); });
    return () => { cancelled = true; };
  }, [id, inList]);

  if (configured === false) return null;

  const malformed = id !== '' && !isOrgId(id);

  return (
    <div className="spatial-admin-row">
      <label>Organization (GeoLink)</label>
      <select
        className="spatial-admin-select"
        value={inList ? id : ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— None (owned by the wallet directly) —</option>
        {orgs.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name || o.id.slice(0, 8)}
            {' · '}{o.alpha2 || o.numericCode || '—'}
            {o.statusName !== 'active' ? ` · ${o.statusName}` : ''}
          </option>
        ))}
      </select>
      <input
        className="spatial-admin-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an organization id (64 hex characters)"
        style={{ marginTop: 4, fontFamily: 'monospace' }}
      />
      {malformed && (
        <div className="spatial-admin-helper" style={{ color: '#f87171' }}>
          An organization id is 64 hex characters.
        </div>
      )}
      {!malformed && id !== '' && !inList && resolved && (
        <div className="spatial-admin-helper">
          {resolved.name || 'Unnamed'} · {resolved.alpha2 || resolved.numericCode} · {resolved.statusName}
        </div>
      )}
      {!malformed && id !== '' && !inList && resolved === null && (
        <div className="spatial-admin-helper" style={{ color: '#fbbf24' }}>
          Not found on chain — the object will save, but nothing will resolve this id.
        </div>
      )}
      {!bridgeSafe && id !== '' && (
        <div className="spatial-admin-helper" style={{ color: '#fbbf24' }}>
          This GeoLink deployment does not report a guarded organization
          bridge, so the link will not be saved with the object.
        </div>
      )}
      <div className="spatial-admin-helper">
        Stored as <code>metadata.organization_id</code>, which GeoLink promotes
        into its own column when the organization is mirrored. This is the
        searchable pointer only — it carries no relationship, no approval and
        no signature. The on-chain binding below is the authoritative one, and
        it writes this field for you.
      </div>
    </div>
  );
};

const EditorTab: React.FC<EditorTabProps> = (p) => {
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => p.setForm(prev => ({ ...prev, [k]: v }));
  const vectorInputRef = useRef<HTMLInputElement | null>(null);
  // Diagnostic: surfaces what's actually in the tier array so a
  // visually-empty list reveals itself as either zero entries (the
  // section header should hide in that case) or malformed entries we
  // can fix in formFromRow.
  if (p.form.tiers.length > 0) {
  }
  return (
    <>
      <div className="spatial-admin-section">
        <h3>Identity</h3>
        <div className="spatial-admin-row">
          <label>Name *</label>
          <input className="spatial-admin-input" value={p.form.name} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div className="spatial-admin-row">
          <label>Type</label>
          <select className="spatial-admin-select" value={p.form.object_type} onChange={(e) => set('object_type', e.target.value as SpatialObjectType)}>
            {OBJECT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="spatial-admin-row">
          <label>Visibility</label>
          <select className="spatial-admin-select" value={p.form.visibility} onChange={(e) => set('visibility', e.target.value as SpatialObjectVisibility)}>
            <option value="public">Public</option>
            <option value="restricted">Restricted</option>
            <option value="private">Private</option>
          </select>
        </div>
        <div className="spatial-admin-row">
          <label>Owner wallet</label>
          <input
            className="spatial-admin-input"
            value={p.form.owner_wallet}
            onChange={(e) => set('owner_wallet', e.target.value)}
            placeholder={p.walletAddress ? `Defaults to ${p.walletAddress.slice(0, 6)}…${p.walletAddress.slice(-4)}` : 'leave blank for default'}
          />
          <div className="spatial-admin-helper">GeoLink defaults this to the API key's public_key when blank.</div>
        </div>
        <div className="spatial-admin-row">
          <label>Description</label>
          <textarea className="spatial-admin-textarea" style={{ minHeight: 60 }} value={p.form.description} onChange={(e) => set('description', e.target.value)} />
        </div>
      </div>

      <div className="spatial-admin-section">
        <h3>Geometry</h3>
        {p.form.geometry_provenance && (() => {
          const prov = p.form.geometry_provenance;
          return (
            <div
              className="spatial-admin-row"
              style={{
                flexDirection: 'column', alignItems: 'stretch', gap: 2,
                fontSize: 11, opacity: 0.8, lineHeight: 1.4,
              }}
            >
              <div>
                {attributionFor(prov.source)}
                {' · '}
                <a
                  href={OSM_LICENSE_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: 'inherit' }}
                >
                  licence
                </a>
              </div>
              <div style={{ fontFamily: 'monospace' }}>
                {prov.source}
                {prov.sourceIds.length > 0 ? ` · osm ${prov.sourceIds.join(', ')}` : ''}
                {' · heights '}{prov.heightProvenance}
                {prov.importedAt ? ` · imported ${prov.importedAt.slice(0, 10)}` : ''}
              </div>
            </div>
          );
        })()}
        <div className="spatial-admin-quickbar">
          <button className="spatial-admin-button" onClick={() => p.startDraw('draw_polygon')}>✏️ Polygon</button>
          <button className="spatial-admin-button" onClick={() => p.startDraw('draw_point')}>📍 Point</button>
          <button className="spatial-admin-button" onClick={() => p.startDraw('draw_line_string')}>〰️ Line</button>
          <button className="spatial-admin-button" onClick={p.stopDraw}>✋ Stop</button>
        </div>
        {p.importOffer && (() => {
          const { structures } = p.importOffer;
          const picked = structures.filter((s) => p.importPicked.includes(s.id));
          const merged = picked.length > 0 ? mergeStructures(picked) : null;
          return (
            <div
              className="spatial-admin-row"
              style={{
                flexDirection: 'column', gap: 6, alignItems: 'stretch',
                padding: '8px 10px', borderRadius: 6, fontSize: 12,
                background: 'rgba(56,189,248,0.10)',
                border: '1px solid rgba(56,189,248,0.40)',
                color: '#bae6fd', lineHeight: 1.4,
              }}
            >
              <div>
                OpenStreetMap maps <b>{structures.length}</b> separate structures here.
                {' '}It does not say whether they are one landmark, so pick the ones
                {' '}that belong to this object.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {structures.map((s) => {
                  const on = p.importPicked.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      style={{
                        display: 'flex', alignItems: 'baseline', gap: 6,
                        fontFamily: 'monospace', cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => p.onTogglePicked(s.id)}
                      />
                      <span>
                        {s.name ?? (s.clicked ? 'clicked structure' : 'unnamed')}
                        {' · '}{s.heightMeters}m
                        {' · '}{s.tiers.length}
                        {s.tiers.length === 1 ? ' polygon' : ' polygons'}
                        {s.clicked ? ' · clicked' : ` · ${Math.round(s.distanceMeters)}m away`}
                        {s.heightProvenance !== 'measured'
                          ? ` · height ${s.heightProvenance}`
                          : ''}
                        {` · osm ${s.kind.slice(0, 3)} ${s.id}`}
                      </span>
                    </label>
                  );
                })}
              </div>
              {merged && (
                <div style={{ opacity: 0.85 }}>
                  Selected: {merged.polygons.length}
                  {merged.polygons.length === 1 ? ' polygon' : ' polygons'},
                  {' '}{merged.baseHeightMeters}m → {merged.heightMeters}m.
                  {merged.heightProvenance !== 'measured'
                    ? ` Heights are ${merged.heightProvenance} — check them before saving.`
                    : ''}
                </div>
              )}
              <div style={{ opacity: 0.7, fontSize: 11 }}>{OSM_ATTRIBUTION}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="spatial-admin-button" onClick={p.onApplyPicked}>
                  Use selected
                </button>
                <button className="spatial-admin-button" onClick={p.onDismissOffer}>
                  Cancel
                </button>
              </div>
            </div>
          );
        })()}
        <div className="spatial-admin-quickbar">
          <button
            className="spatial-admin-button"
            onClick={p.startImport}
            title="Click a building on the map. OpenStreetMap supplies the footprint and per-tier heights automatically; a live 3D preview appears on the map."
          >🏢 Import 3D building</button>
          <button className="spatial-admin-button" onClick={p.usePlayer}>📍 Use my location</button>
          <button
            className="spatial-admin-button"
            onClick={p.useMapCenter}
            title="Set Point geometry to the current map centre. Useful for relocating an object that ended up in the wrong place — fly the map to the real spot first."
          >🎯 Use map centre</button>
          <button
            className="spatial-admin-button"
            onClick={() => vectorInputRef.current?.click()}
            title="Import geometry from a GeoJSON, KML, GPX, or zipped Shapefile."
          >📂 Import vector</button>
          <input
            ref={vectorInputRef}
            type="file"
            accept={VECTOR_IMPORT_ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) p.importVector(file);
              e.target.value = '';
            }}
          />
        </div>
        {p.mapboxToken && (
          <div className="spatial-admin-row">
            <label>Address search</label>
            <div style={{ display: 'flex', gap: 6, width: '100%' }}>
              <input className="spatial-admin-input" value={p.search} onChange={(e) => p.setSearch(e.target.value)} placeholder="123 Main St…" onKeyDown={(e) => { if (e.key === 'Enter') p.flyToAddress(); }} />
              <button className="spatial-admin-button" onClick={p.flyToAddress}>🔍</button>
            </div>
          </div>
        )}
        {/* Placeholder-geometry warning. Buildings, floors, zones and parcels
            should normally have polygon footprints — a bare Point usually
            means the operator forgot to run "Import 3D building" or "Polygon"
            after the country-tooltip quick-add (which originally pre-filled a
            Point at the country centroid). Surface this loudly so it doesn't
            silently slip through to a saved object. */}
        {(() => {
          let parsed: { type?: string } | null = null;
          try { parsed = p.form.geometry ? JSON.parse(p.form.geometry) : null; } catch { parsed = null; }
          const isPoint     = parsed?.type === 'Point';
          const polygonType = ['building', 'floor', 'zone', 'real_estate'].includes(p.form.object_type as string);
          if (!isPoint || !polygonType) return null;
          return (
            <div
              style={{
                margin: '6px 0',
                padding: '8px 10px',
                borderRadius: 6,
                fontSize: 12,
                background: 'rgba(245, 158, 11, 0.12)',
                border: '1px solid rgba(245, 158, 11, 0.45)',
                color: '#fcd34d',
                lineHeight: 1.4,
              }}
            >
              ⚠ This <b>{p.form.object_type}</b> only has a Point geometry. Buildings / zones / floors / parcels usually need a polygon footprint —
              tap <b>🏢 Import 3D building</b> over the real building, or use <b>✏️ Polygon</b> to draw it. The Point is just a placeholder.
            </div>
          );
        })()}
        <div className="spatial-admin-row">
          <label>Geometry (GeoJSON)</label>
          <textarea className="spatial-admin-textarea" value={p.form.geometry} onChange={(e) => set('geometry', e.target.value)} placeholder='{"type":"Polygon","coordinates":[[...]]} or {"type":"Point","coordinates":[lng,lat]}' />
        </div>
      </div>

      <div className="spatial-admin-section">
        <h3>Heights & links</h3>
        <div className="spatial-admin-row" style={{ flexDirection: 'row' }}>
          <div style={{ flex: 1 }}>
            <label>Base height (m)</label>
            <input className="spatial-admin-input" type="number" value={p.form.base_height_meters} onChange={(e) => set('base_height_meters', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label>Height (m)</label>
            <input className="spatial-admin-input" type="number" value={p.form.height_meters} onChange={(e) => set('height_meters', e.target.value)} />
          </div>
        </div>
        {p.form.tiers.length > 0 && (
          <div className="spatial-admin-row" style={{ flexDirection: 'column', gap: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>
                Per-polygon heights
                {' '}({p.form.tiers.length}
                {p.form.tiers.length === 1 ? ' polygon' : ' polygons'})
              </span>
              <button
                type="button"
                onClick={() => set('tiers', [])}
                title="Drop the per-polygon heights and extrude the whole object between its own base and height"
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 4,
                  background: 'transparent', color: '#f87171',
                  border: '1px solid rgba(248,113,113,0.35)', cursor: 'pointer',
                }}
              >
                Clear tiers
              </button>
            </label>
            <div
              style={{
                fontSize: 11, color: 'rgba(255,255,255,0.85)',
                background: 'rgba(0,0,0,0.35)',
                padding: '6px 8px', borderRadius: 4,
                maxHeight: 140, overflowY: 'auto', fontFamily: 'monospace',
                lineHeight: 1.4,
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {p.form.tiers.map((t, i) => {
                // Defensive coercion in case a record persisted from
                // an older session has missing fields. Keep the tier
                // visible so the operator can still see the row count
                // even if min/max numbers are absent.
                const idx = Number.isFinite(t?.polygon_index) ? t.polygon_index : i;
                const mn  = Number.isFinite(t?.min_height) ? Number(t.min_height) : 0;
                const mx  = Number.isFinite(t?.height)     ? Number(t.height)     : 0;
                const span = mx - mn;
                return (
                  <div key={i}>
                    #{i} · poly {idx}: {mn}m → {mx}m
                    {' '}({span.toFixed(1)}m tall)
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
              Each polygon extrudes between its own min/max height. Captured at
              import time from the underlying 3D building tile data so stepped
              towers (Burj Khalifa, Empire State, …) render with their setbacks
              instead of as one fat block.
            </div>
          </div>
        )}
        <div className="spatial-admin-row" style={{ flexDirection: 'row' }}>
          <div style={{ flex: 1 }}>
            <label>Floor #</label>
            <input className="spatial-admin-input" type="number" value={p.form.floor_number} onChange={(e) => set('floor_number', e.target.value)} placeholder="(optional)" />
          </div>
          <div style={{ flex: 1 }}>
            <label>Color</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="color"
                value={/^#[0-9a-f]{6}$/i.test(p.form.color_key) ? p.form.color_key : '#4A90E2'}
                onChange={(e) => set('color_key', e.target.value)}
                style={{ width: 36, height: 30, padding: 0, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 4, background: 'transparent' }}
                title="Pick color"
              />
              <input
                className="spatial-admin-input"
                value={p.form.color_key}
                onChange={(e) => set('color_key', e.target.value)}
                placeholder="#4A90E2"
                style={{ flex: 1 }}
              />
            </div>
          </div>
        </div>
        <EventFields form={p.form} set={set} />
        <ParentPicker
          form={p.form}
          candidates={p.parentCandidates}
          selectedParent={p.selectedParent}
          onChange={(id) => set('parent_object_id', id)}
          onPickOnMap={p.startPickParent}
          canPickOnMap={p.canPickParentOnMap}
        />
        <OrganizationPicker
          value={p.form.organization_id}
          onChange={(id) => set('organization_id', id)}
        />
        {/*
          Only for a saved object: the on-chain id IS the GeoLink UUID, so
          there is nothing to bind to until GeoLink has issued one.
        */}
        {(p.form.id ?? '').trim() && (
          <SpatialObjectOrganizationPanel
            objectId={(p.form.id ?? '').trim()}
            objectName={p.form.name || null}
            objectType={p.form.object_type}
            countryIso2={p.countryIso2}
            parentObjectId={p.form.parent_object_id.trim() || null}
            geoLinkOrganizationId={p.form.organization_id.trim() || null}
            signer={p.signer}
            walletAddress={p.walletAddress}
            networkPassphrase={p.networkPassphrase}
            canAdminister={p.isAdmin}
          />
        )}
        {/*
          The debugging view for this object. Sits next to the binding panel
          because the two answer adjacent questions: that one says who owns
          the object, this one says what that means for the wallet in front
          of it.
        */}
        {(p.form.id ?? '').trim() && (
          <div style={{ margin: '10px 0' }}>
            <AuthorityPanel
              auth={p.formAuthorization.auth}
              loading={p.formAuthorization.loading}
              error={p.formAuthorization.error}
              wallet={p.walletAddress}
              objectName={p.form.name || null}
            />
          </div>
        )}
        <div className="spatial-admin-row">
          <label>Interaction types (comma)</label>
          <input className="spatial-admin-input" value={p.form.interaction_types} onChange={(e) => set('interaction_types', e.target.value)} placeholder="tap,info,execute" />
        </div>
      </div>

      <div className="spatial-admin-section">
        <h3>Execution rule</h3>
        <div className="spatial-admin-row">
          <label>Rule</label>
          <select className="spatial-admin-select" value={p.form.linked_execution_rule_id} onChange={(e) => p.onRuleChange(e.target.value)}>
            <option value="">— None —</option>
            {p.rules.map(r => (
              <option key={String(r.id)} value={String(r.id)}>
                #{r.id} · {r.rule_name ?? r.ruleName ?? '(unnamed)'} · {r.function_name ?? r.functionName ?? '?'}
              </option>
            ))}
          </select>
          <div className="spatial-admin-helper">Selecting a rule auto-fills the contract + function below.</div>
        </div>
        <div className="spatial-admin-row">
          <label>Linked contract address</label>
          <input className="spatial-admin-input" value={p.form.linked_contract_address} onChange={(e) => set('linked_contract_address', e.target.value)} placeholder="C…" />
        </div>
        <div className="spatial-admin-row">
          <label>Linked function name</label>
          <input className="spatial-admin-input" value={p.form.linked_contract_function} onChange={(e) => set('linked_contract_function', e.target.value)} placeholder="execute_payment" />
        </div>
      </div>

      {/*
        Gated by the same rule as the rest of the editor, not by
        `canManageObject` alone. Presence is a setting on the object, so
        anyone who may change what the object is may change what it says —
        and an object the chain has never heard of has no `canManageObject`
        to grant, which previously left its whole presence panel read-only
        with no explanation while every other field stayed editable.

        The narrower check still exists where it decides something: the
        object cannot actually act until `verifyStandingAuthority`
        re-reads manage authority from the chain at execution time. That
        is a runtime gate, not an editing one.
      */}
      <SpatialPresenceSections
        objectId={(p.form.id ?? '').trim()}
        objectName={p.form.name || null}
        walletAddress={p.walletAddress}
        canManage={creatorMayEdit(
          p.form.created_by_wallet,
          p.formAuthorization.auth,
          p.walletAddress,
          p.isAdmin,
        )}
        authLoading={p.formAuthorization.loading}
        canActAutonomously={p.formAuthorization.auth?.canManageObject === true}
        rules={p.rules}
        countryCode={p.countryIso2}
        storedFlag={
          (p.form.metadata_extra?.surface_flag ?? null) as StoredFlagIntent | null
        }
        // The binding signer, adapted: a treasury payout is the same
        // kind of write, and the overlay already holds the one wallet
        // wrapper the app hands it.
        signXdr={p.signer
          ? (xdr) => p.signer!.signTransaction(xdr, p.networkPassphrase)
          : null}
      />

      <SpatialObjectSimulator
        objectId={(p.form.id ?? '').trim()}
        objectName={p.form.name || null}
        walletAddress={p.walletAddress}
        canManage={creatorMayEdit(
          p.form.created_by_wallet,
          p.formAuthorization.auth,
          p.walletAddress,
          p.isAdmin,
        )}
        anchor={formAnchor(p.form.geometry)}
      />
    </>
  );
};

/**
 * A starting point for the simulated participant, from the geometry in
 * the form rather than the saved row — an operator who has just moved
 * the object should simulate against where it is now.
 */
function formAnchor(geometryText: string): { lat: number; lng: number } | null {
  if (!geometryText.trim()) return null;
  try {
    const bounds = geometryBounds(JSON.parse(geometryText));
    if (!bounds) return null;
    return {
      lat: (bounds.south + bounds.north) / 2,
      lng: (bounds.west + bounds.east) / 2,
    };
  } catch {
    return null;
  }
}

interface PreviewTabProps {
  previewProvider: SpatialRenderProvider;
  setPreviewProvider: (v: SpatialRenderProvider) => void;
  previewObjectId: string;
  setPreviewObjectId: (v: string) => void;
  previewEnvelope: SpatialObjectsRenderEnvelope | null;
  onLoad: () => void;
}

const PreviewTab: React.FC<PreviewTabProps> = (p) => (
  <>
    <div className="spatial-admin-section">
      <div className="spatial-admin-row">
        <label>Provider</label>
        <select className="spatial-admin-select" value={p.previewProvider} onChange={(e) => p.setPreviewProvider(e.target.value as SpatialRenderProvider)}>
          {PROVIDERS.map(prov => <option key={prov} value={prov}>{prov}</option>)}
        </select>
      </div>
      <div className="spatial-admin-row">
        <label>Object id (optional)</label>
        <input className="spatial-admin-input" value={p.previewObjectId} onChange={(e) => p.setPreviewObjectId(e.target.value)} placeholder="leave blank for full collection" />
      </div>
      <div className="spatial-admin-quickbar">
        <button className="spatial-admin-button primary" onClick={p.onLoad}>↻ Load preview</button>
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.4, marginTop: 4 }}>
        Renders the same canonical GeoJSON on Mapbox GL JS (when{' '}
        <code>provider=mapbox</code> + Mapbox token) or MapLibre GL JS
        (every other provider, including MapTiler tiles when a key is
        configured). Use this to verify how an object will look across
        providers before publishing.
      </div>
    </div>

    {p.previewEnvelope && (
      <div className="spatial-admin-section">
        <h3>Preview map</h3>
        <SpatialPreviewMap
          provider={p.previewProvider}
          envelope={p.previewEnvelope}
          height={360}
        />
      </div>
    )}

    {p.previewEnvelope && (
      <>
        <div className="spatial-admin-section">
          <h3>FeatureCollection ({p.previewEnvelope.featureCollection.features.length} features)</h3>
          <pre className="spatial-admin-json">{JSON.stringify(p.previewEnvelope.featureCollection, null, 2)}</pre>
        </div>
        {p.previewEnvelope.style && (
          <div className="spatial-admin-section">
            <h3>Suggested style block</h3>
            <pre className="spatial-admin-json">{JSON.stringify(p.previewEnvelope.style, null, 2)}</pre>
          </div>
        )}
      </>
    )}
  </>
);

interface TesterTabProps {
  rows: SpatialObjectAdminRow[];
  agents: CachedAgentRecord[];
  walletAddress: string | null;
  objectId: string;
  setObjectId: (v: string) => void;
  actor: string;
  setActor: (v: string) => void;
  interaction: string;
  setInteraction: (v: string) => void;
  result: SpatialObjectInteractResponse | null;
  onRun: () => void;
}

const TesterTab: React.FC<TesterTabProps> = (p) => {
  const selectedRow = useMemo(() => p.rows.find(r => r.id === p.objectId) ?? null, [p.rows, p.objectId]);
  const interactionOptions = useMemo(() => {
    const meta = (selectedRow?.metadata as { interaction_types?: unknown } | null | undefined) ?? null;
    const types = Array.isArray(meta?.interaction_types) ? (meta!.interaction_types as string[]) : [];
    return types.length > 0 ? types : ['tap', 'info', 'execute'];
  }, [selectedRow]);

  const interactionAny = (p.result?.interaction ?? null) as
    | { decision?: InteractDecision; decisionReason?: string | null; decision_reason?: string | null }
    | null;
  const decision: InteractDecision | null = interactionAny?.decision ?? null;
  const decisionReason: string | null =
    interactionAny?.decisionReason ?? interactionAny?.decision_reason ?? null;

  return (
    <>
      <div className="spatial-admin-section">
        <h3>Test an interaction</h3>
        <div className="spatial-admin-helper">
          Calls <code>/api/admin/spatial-objects/:id/test-interaction</code>. The
          decision matches what the public <code>/interact</code> endpoint would
          return — but nothing is recorded and no execution is authorised.
        </div>
        <div className="spatial-admin-row">
          <label>Spatial object</label>
          <select className="spatial-admin-select" value={p.objectId} onChange={(e) => p.setObjectId(e.target.value)}>
            <option value="">— Pick one —</option>
            {p.rows.map(r => (
              <option key={r.id} value={r.id}>
                {(r.name || r.id.slice(0, 8))} · {r.objectType}
              </option>
            ))}
          </select>
        </div>
        <div className="spatial-admin-row">
          <label>Actor wallet</label>
          <input className="spatial-admin-input" value={p.actor} onChange={(e) => p.setActor(e.target.value)} placeholder={p.walletAddress ? `Defaults to ${p.walletAddress.slice(0, 6)}…` : 'G… or C…'} />
        </div>
        {p.agents.length > 0 && (
          <div className="spatial-admin-row">
            <label>…or pick one of your agents</label>
            <select className="spatial-admin-select" value="" onChange={(e) => { if (e.target.value) p.setActor(e.target.value); }}>
              <option value="">— Use my agent —</option>
              {p.agents.map(a => (
                <option key={a.address} value={a.address}>
                  {(a.address.slice(0, 6))}…{a.address.slice(-4)} · {a.kindLabel ?? 'agent'}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="spatial-admin-row">
          <label>Interaction type</label>
          <select className="spatial-admin-select" value={p.interaction} onChange={(e) => p.setInteraction(e.target.value)}>
            {interactionOptions.map(it => <option key={it} value={it}>{it}</option>)}
          </select>
        </div>
        <div className="spatial-admin-quickbar">
          <button className="spatial-admin-button primary" onClick={p.onRun}>▶ Run</button>
        </div>
      </div>
      {p.result && (
        <div className="spatial-admin-section">
          <h3>Result</h3>
          {decision && (
            <div className="spatial-admin-row">
              <span className={`spatial-admin-pill ${decision === 'allowed' ? 'ok' : 'warn'}`}>{decision}</span>
              {decisionReason && <span style={{ marginLeft: 8, fontSize: 12, color: '#374151' }}>{String(decisionReason)}</span>}
            </div>
          )}
          <pre className="spatial-admin-json">{JSON.stringify(p.result, null, 2)}</pre>
        </div>
      )}
    </>
  );
};

export default SpatialObjectsAdminOverlay;
